import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';
import { OrderStatus } from '../types';
import { ShippingTab } from '../components/orders/ShippingTab';
import { WorkflowProgressTab } from '../components/orders/WorkflowProgressTab';
import { InvoiceTab } from '../components/orders/InvoiceTab';
import { LaborBomTab } from '../components/orders/LaborBomTab';

// Tab types
type OrderTab = 'details' | 'cutsheet' | 'bom' | 'shipping' | 'qc' | 'cost' | 'progress' | 'invoice' | 'labor';

// Label formatting utilities - clean display names
const formatRemoteDescription = (raw: string): string => {
  const map: Record<string, string> = {
    'alpha_pioneer_1ch': 'Alpha Pioneer 1-Channel',
    'alpha_pioneer_4ch': 'Alpha Pioneer 4-Channel',
    'alpha_pioneer_8ch_lcd': 'Alpha Pioneer 8-Ch LCD',
    'alpha_pioneer_16ch_lcd': 'Alpha Pioneer 16-Ch LCD',
    'somfy_situo_1ch': 'Somfy Situo 1-Channel',
    'somfy_situo_5ch': 'Somfy Situo 5-Channel',
    'somfy_telis_1ch': 'Somfy Telis 1-Channel',
    'somfy_telis_4ch': 'Somfy Telis 4-Channel',
    'somfy_telis_16ch': 'Somfy Telis 16-Channel',
  };
  return map[raw?.toLowerCase()] || raw?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'None';
};

const formatInstallationType = (value: string | undefined): string => {
  if (!value) return 'Wall';
  const map: Record<string, string> = {
    'face_of_header': 'Wall',
    'face of_header': 'Wall',
    'wall': 'Wall',
    'under_header': 'Ceiling',
    'ceiling': 'Ceiling',
    'recessed': 'Recessed (Pocket)',
    'recessed_pocket': 'Recessed (Pocket)',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatTrackMount = (value: string | undefined): string => {
  if (!value) return 'Face';
  const map: Record<string, string> = {
    'face': 'Face',
    'inside': 'Inside',
    'inside_face_left': 'Inside/Face Left',
    'inside_face_right': 'Inside/Face Right',
    'inside_outside_left': 'Inside/Outside Left',
    'inside_outside_right': 'Inside/Outside Right',
    'recessed': 'Recessed',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatUChannel = (value: string | undefined): string => {
  if (!value || value === 'none') return 'None';
  const map: Record<string, string> = {
    'none': 'None',
    'both_sides': 'Both Sides',
    'two_sides': 'Both Sides',
    'left_side': 'Left Side Only',
    'left_side_only': 'Left Side Only',
    'right_side': 'Right Side Only',
    'right_side_only': 'Right Side Only',
    'one_side': 'One Side',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatShippingMethod = (value: string | undefined): string => {
  if (!value) return 'Not specified';
  const map: Record<string, string> = {
    'willcall': 'Will Call (Pickup)',
    'will_call': 'Will Call (Pickup)',
    'freight': 'Freight Shipping',
    'ltl_freight': 'LTL Freight',
    'ship_to_dealer': 'Ship to Dealer Address',
    'use_customer_address': 'Use Customer Address',
    'different_address': 'Different Address',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatFabricType = (value: string | undefined): string => {
  if (!value) return '-';
  const map: Record<string, string> = {
    'solar94': 'Solar',
    'solar99': 'Premium Solar',
    'insect': 'Insect',
    'cvw': 'Clear View',
    'omega_pro_3': 'Omega Pro 3%',
    'visiontex_94': 'VisionTex 94',
    'visiontex_99': 'VisionTex 99',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatBrushSize = (value: string | undefined): string => {
  if (!value) return '1 inch';
  if (value === '1' || value === '1"' || value === '1 inch') return '1 inch';
  if (value === '2' || value === '2"' || value === '2 inch') return '2 inch';
  return value;
};

// Helper to open installer sheet with authentication
const openInstallerSheet = async (orderId: string, itemNumber: number) => {
  try {
    const token = localStorage.getItem('admin_auth_token');
    if (!token) {
      alert('Please log in to view installer sheets.');
      return;
    }

    const response = await fetch(`/api/bom/installer-sheet/${orderId}/${itemNumber}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      alert('Session expired. Please log in again.');
      return;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Installer sheet error:', response.status, errorText);
      throw new Error(`Failed to fetch installer sheet: ${response.status}`);
    }

    const html = await response.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error opening installer sheet:', error);
    alert('Failed to load installer sheet. Please try again.');
  }
};

interface OrderItem {
  id: string;
  orderId: string;
  itemNumber: number;
  productType: string;
  widthTop?: number;
  widthMiddle?: number;
  widthBottom?: number;
  heightLeft?: number;
  heightCenter?: number;
  heightRight?: number;
  maxWidth: number;
  maxHeight: number;
  motorSide: string;
  uChannel: string;
  uChannelColor?: string;
  expertMode?: boolean;
  biasedCut: boolean;
  biasedCutInches?: number;
  biasedCutSide?: string;
  installationType?: string;
  recessed?: boolean;
  trackMount?: string;
  systemColor?: string;
  componentColors?: {
    housingColor?: string;
    leftTrackColor?: string;
    rightTrackColor?: string;
    weightBarColor?: string;
    leftUChannelColor?: string;
    rightUChannelColor?: string;
    housingRalNumber?: string;
    leftTrackRalNumber?: string;
    rightTrackRalNumber?: string;
    weightBarRalNumber?: string;
    leftUChannelRalNumber?: string;
    rightUChannelRalNumber?: string;
  } | null;
  fabricType?: string;
  fabricColor?: string;
  motorType?: string;
  motorModel?: string;
  boxSize?: string;
  boxColor?: string;
  weightBarSize?: string;
  weightBarColor?: string;
  weightBarBrush?: string;
  kederColor?: string;
  springs?: string;
  springCount?: number;
  angledBottom?: boolean;
  seam?: boolean;
  notes?: string;
  unitPrice: number;
  uChannelPrice?: number;
  extrasPrice?: number;
  totalPrice: number;
  windowGroup?: string;
  windowPanel?: boolean;
  windowOrientation?: string;
  windowCount?: number;
  windowWidth?: number;
  windowHeight?: number;
  topBorderInches?: number;
  sideBorderInches?: number;
  bottomBorderInches?: number;
  screenLocation?: string;
  status?: string;
  brushSize?: string;
}

interface OrderData {
  id: string;
  orderNumber: string;
  orderType?: string;
  dealerId: string;
  dealerContactId?: string;
  companyName?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  discountRate?: number;
  sidemark?: string;
  poNumber?: string;
  notes?: string;
  internalNotes?: string;
  status: OrderStatus;
  blocker?: string;
  blockerNote?: string;
  requestedShipDate?: string;
  neededByDate?: string;
  estimatedShipDate?: string;
  actualShipDate?: string;
  shipToAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    name?: string;
    contactName?: string;
  } | string;
  shippingMethod?: string;
  shippingCarrier?: string;
  shippingCost?: number;
  trackingNumber?: string;
  liftgateDelivery?: boolean;
  residentialDelivery?: boolean;
  insideDelivery?: boolean;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  packagingAmount: number;
  shippingAmount: number;
  totalAmount: number;
  createdBy?: string;
  orderedBy?: string;
  authorizedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  releasedAt?: string;
  shippedAt?: string;
  items?: OrderItem[];
  deposit_paid?: boolean;
  depositPaid?: boolean;
  credit_approved?: boolean;
  creditApproved?: boolean;
  stripeSessionId?: string;
  stripe_session_id?: string;
  order_data?: any;
  orderData?: any;
  // Customer info
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerStreet?: string;
  customerCity?: string;
  customerState?: string;
  customerZip?: string;
  // Controls & Accessories
  motorType?: string;
  remoteType?: string;
  controllerType?: string;
  remoteQty?: number;
  controllerQty?: number;
  wallSwitchQty?: number;
  // Freight data from shipping portal (camelCase from API, snake_case fallback)
  shippingMethod?: string;
  shipping_method?: string;
  shipToAddress?: any;
  shipping_address?: any;
  selectedQuoteId?: string;
  selected_quote_id?: string;
  selectedCarrierName?: string;
  selected_carrier_name?: string;
  selectedCarrierCode?: string;
  selected_carrier_code?: string;
  freightBaseAmount?: number;
  freight_base_amount?: number;
  freightMarkupPercent?: number;
  freight_markup_percent?: number;
  freightMarkupAmount?: number;
  freight_markup_amount?: number;
  freightTotalAmount?: number;
  freight_total_amount?: number;
  freightQuotes?: any[];
  freight_quotes?: any[];
  freightviewShipmentId?: string;
  freightview_shipment_id?: string;
  freightBookedAt?: string;
  freight_booked_at?: string;
  freightBolNumber?: string;
  freight_bol_number?: string;
  freightProNumber?: string;
  freight_pro_number?: string;
  freightPickupConfirmation?: string;
  freight_pickup_confirmation?: string;
  // Admin edit audit
  adminEditedAt?: string;
  admin_edited_at?: string;
  adminEditedBy?: string;
  admin_edited_by?: string;
  // Payment dates
  depositPaidAt?: string;
  deposit_paid_at?: string;
  paidInFull?: boolean;
  paid_in_full?: boolean;
  paidInFullAt?: string;
  paid_in_full_at?: string;
  creditApprovedAt?: string;
  credit_approved_at?: string;
  creditTransactionId?: string;
}

const STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ['submitted'],
  pending_payment: ['submitted'],
  submitted: ['pending_release', 'in_production', 'cancelled'],
  pending_release: ['approved', 'in_production', 'cancelled'],
  needs_correction: ['submitted', 'cancelled'],
  approved: ['in_production', 'cancelled'],
  in_production: ['ready_to_ship', 'cancelled'],
  ready_to_ship: ['shipped', 'cancelled'],
  shipped: ['delivered', 'closed', 'cancelled'],
  delivered: ['closed', 'cancelled'],
  closed: [],
  cancelled: [],
};

/**
 * Recommended bias (angled bottom) from the three height measurements —
 * mirrors the dealer portal's calculateRecommendedBias. Longer side = the
 * taller measurement; amount = max−min difference, capped at 4", ignored
 * under 0.25". Needs at least two measurements.
 */
function calcRecommendedBias(left: number, center: number, right: number): {
  needsBias: boolean; biasAmount: number; longerSide: 'left' | 'right';
} {
  const valid = [left, center, right].filter(h => h > 0);
  if (valid.length < 2) return { needsBias: false, biasAmount: 0, longerSide: 'left' };
  const maxH = Math.max(left, center, right);
  const minH = Math.min(...valid);
  const diff = maxH - minH;
  if (diff < 0.25) return { needsBias: false, biasAmount: 0, longerSide: 'left' };
  const biasAmount = Math.round(Math.min(diff, 4) * 10000) / 10000;
  const longerSide: 'left' | 'right' = left >= right ? 'left' : 'right';
  return { needsBias: true, biasAmount, longerSide };
}

/** Apply auto-calculated bias to the item-edit form when bias cut is on. */
function withAutoBias(form: any): any {
  if (!form.biasedCut) return form;
  const b = calcRecommendedBias(
    Number(form.heightLeft) || 0, Number(form.heightCenter) || 0, Number(form.heightRight) || 0,
  );
  return b.needsBias
    ? { ...form, biasedCutInches: b.biasAmount, biasedCutSide: b.longerSide }
    : { ...form, biasedCutInches: 0 };
}

/**
 * Box-width adjustment = how much wider the housing box is vs the system width.
 * For inside-mount, system width uses the SMALLEST opening (so fabric/tracks fit
 * the narrowest point) but the box lives at the TOP of the opening — so the box
 * is min + (top − min), capped at +0.75". Mirrors the dealer portal's
 * calculateRecommendedBoxAdjustment so factory edits match how orders were priced.
 * Only applies for inside-type mounts (face/wall use the largest width directly).
 */
const INSIDE_MOUNTS = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'];
function calcBoxAdjustment(wT: number, wM: number, wB: number): number {
  const valid = [wT, wM, wB].filter(w => w > 0);
  if (valid.length < 2) return 0;
  const minW = Math.min(...valid);
  if (wT > minW) return Math.min(wT - minW, 0.75);       // top wider → box larger, cap +0.75"
  if (wT > 0 && wT < minW) return -(minW - wT);          // rare: top narrower → box smaller
  return 0;
}
/** Recompute box_width_adjustment from the widths when editing an inside-mount unit. */
function withAutoBox(form: any): any {
  // Non-inside mounts (face/wall) use the largest width directly — no top-vs-min adjustment.
  if (!INSIDE_MOUNTS.includes(form.trackMount || '')) return { ...form, boxWidthAdjustment: 0 };
  const adj = calcBoxAdjustment(Number(form.widthTop) || 0, Number(form.widthMiddle) || 0, Number(form.widthBottom) || 0);
  return { ...form, boxWidthAdjustment: adj };
}

/** Map a stored motor_type value to its display label. Several values mean the
 *  same motor — notably 'keder screens' and 'keder' (216 orders use the former),
 *  which must both read as "Keder" rather than falling through to Alpha. */
function motorLabel(v?: string | null): string {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'keder' || s === 'keder screens') return 'Keder';
  if (s === 'alpha') return 'Alpha';
  if (s === 'somfy') return 'Somfy';
  if (s === 'no motor' || s === 'none') return 'No Motor';
  return v!.replace(/\b\w/g, (c) => c.toUpperCase()); // title-case anything else
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  // Production scheduling (opened by ?edit=schedule from the orders list)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [schedulePriority, setSchedulePriority] = useState('normal');

  // Load employees once for the support-commission picker
  useEffect(() => {
    api.employees.list().then(setEmployees).catch(() => setEmployees([]));
  }, []);

  // Open the schedule editor when arriving via the "Edit Schedule" button.
  useEffect(() => {
    if (searchParams.get('edit') === 'schedule' && order && !showScheduleModal) {
      const o: any = order;
      const sched = o.scheduledProductionDate || o.scheduled_production_date || '';
      setScheduleDate(sched ? String(sched).slice(0, 10) : '');
      setSchedulePriority(o.productionPriority || o.production_priority || 'normal');
      setShowScheduleModal(true);
    }
  }, [searchParams, order, showScheduleModal]);

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    if (searchParams.get('edit') === 'schedule') {
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleSaveSchedule = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.production.scheduleOrder(id, scheduleDate || null, schedulePriority);
      await loadOrder();
      closeScheduleModal();
    } catch (e: any) {
      alert(e?.message || 'Failed to save schedule');
    } finally {
      setActionLoading(false);
    }
  };
  const [noteText, setNoteText] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingShipAddress, setEditingShipAddress] = useState(false);
  const [shipAddressForm, setShipAddressForm] = useState({ street: '', city: '', state: '', zip: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_type: 'deposit',
    payment_method: 'check',
    payment_reference: '',
    notes: '',
  });
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundForm, setRefundForm] = useState({
    amount: '',
    method: 'check' as 'check' | 'ach' | 'cash' | 'wire' | 'credit_to_account' | 'other',
    reference: '',
    reason: '',
  });
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionNotes, setCorrectionNotes] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState<OrderTab>('details');
  const [cutSheet, setCutSheet] = useState<any>(null);
  const [bom, setBom] = useState<any[]>([]);
  const [bomLoading, setBomLoading] = useState(false);

  // QC Records state
  const [qcRecords, setQcRecords] = useState<any[]>([]);
  const [qcLoading, setQcLoading] = useState(false);
  const [qcLightbox, setQcLightbox] = useState<string | null>(null);

  // Cost analysis state
  const [costData, setCostData] = useState<any>(null);
  const [costComparison, setCostComparison] = useState<any>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [canViewCost, setCanViewCost] = useState(false);

  // Bin assignment state
  const [showBinModal, setShowBinModal] = useState(false);
  const [availableBins, setAvailableBins] = useState<any[]>([]);
  const [selectedBinId, setSelectedBinId] = useState<string>('');
  const [binsLoading, setBinsLoading] = useState(false);
  const [assignedBin, setAssignedBin] = useState<any>(null);

  // Label printing state
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelType, setLabelType] = useState('final_product');
  const [labelCopies, setLabelCopies] = useState(1);
  const [labelPrinters, setLabelPrinters] = useState<any[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState('');
  const [labelPrinting, setLabelPrinting] = useState(false);
  const [labelResults, setLabelResults] = useState<{ id: string; status: string; message?: string }[]>([]);

  // Linked screen designs
  const [linkedDesigns, setLinkedDesigns] = useState<any[]>([]);
  const [expandedDesign, setExpandedDesign] = useState<string | null>(null);

  // Admin edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Parts-order edit modal state (separate from screen-order edit)
  const [showPartsEditModal, setShowPartsEditModal] = useState(false);
  const [partsEditRows, setPartsEditRows] = useState<any[]>([]);
  const [partsEditSidemark, setPartsEditSidemark] = useState('');
  const [partsEditPo, setPartsEditPo] = useState('');
  const [partsEditSaving, setPartsEditSaving] = useState(false);
  const [partsEditError, setPartsEditError] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  // Item edit modal state
  const [showItemEditModal, setShowItemEditModal] = useState(false);
  const [itemEditForm, setItemEditForm] = useState<any>({});
  const [itemEditSaving, setItemEditSaving] = useState(false);
  const [itemEditError, setItemEditError] = useState<string | null>(null);
  const [showItemEditAdvanced, setShowItemEditAdvanced] = useState(false);

  // Fabric color map (fabric type code → color names)
  const [fabricColorMap, setFabricColorMap] = useState<Record<string, string[]>>({});

  // Accessory edit state
  const [editingAccessories, setEditingAccessories] = useState(false);
  const [accForm, setAccForm] = useState({ remoteType: '', remoteQty: 0, wallSwitchType: '', wallSwitchQty: 0 });
  const [accSaving, setAccSaving] = useState(false);

  useEffect(() => {
    loadOrder();
    // Load fabric catalog for color dropdowns
    api.fabricSettings.getCatalog().then((catalog: any) => {
      const map: Record<string, string[]> = {};
      for (const ft of catalog.fabricTypes || []) {
        map[ft.code] = (ft.colors || []).map((c: any) => c.name);
      }
      setFabricColorMap(map);
    }).catch(() => {});
  }, [id]);

  const loadOrder = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.orders.getOrder(id);
      setOrder(data as unknown as OrderData);

      // Parse order_data if present
      const rawData = (data as any).order_data || (data as any).orderData;
      if (rawData) {
        try {
          const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          setOrderData(parsed);
        } catch (e) {
          console.warn('Could not parse order_data:', e);
        }
      }

      // Try to load assigned bin for this order
      try {
        const allBins = await api.bins.getAll();
        const orderBin = allBins.find((b: any) => b.order_id === id || b.order_number === data.order_number);
        setAssignedBin(orderBin || null);
      } catch (binErr) {
        console.warn('Could not load bin info:', binErr);
      }

      // Fetch linked screen designs (non-blocking)
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const token = localStorage.getItem('admin_auth_token');
      fetch(`${API_BASE}/screen-designs/by-order/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : [])
        .then(designs => setLinkedDesigns(designs))
        .catch(() => { /* supplementary */ });
    } catch (err) {
      console.error('Failed to load order:', err);
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const loadBomData = async () => {
    if (!id) return;
    setBomLoading(true);

    // Load Cut Sheet (independent from BOM)
    try {
      const cutSheetData = await api.bom.getCutSheet(id);
      if (Array.isArray(cutSheetData)) {
        setCutSheet({ items: cutSheetData, generatedAt: new Date().toISOString() });
      } else {
        setCutSheet(cutSheetData);
      }
    } catch (err) {
      console.warn('Could not load cut sheet:', err);
    }

    // Load BOM (independent from cut sheet)
    try {
      const bomData = await api.bom.getBom(id);
      // getBom() returns flat rows from DB - need to group by item_number for BomTab
      // BomTab expects: [{itemNumber: 1, bomItems: [...]}, {itemNumber: 2, bomItems: [...]}]
      if (Array.isArray(bomData) && bomData.length > 0) {
        // Check if already grouped (from generateBomPreview) or flat (from getBom)
        if (bomData[0].bomItems || bomData[0].items) {
          // Already grouped format
          setBom(bomData);
        } else {
          // Flat format from DB - group by item_number
          const grouped: Record<number, any> = {};
          bomData.forEach((row: any) => {
            const itemNum = row.item_number || 1;
            if (!grouped[itemNum]) {
              grouped[itemNum] = { itemNumber: itemNum, bomItems: [] };
            }
            grouped[itemNum].bomItems.push({
              partNumber: row.part_number,
              description: row.description,
              componentType: row.component_type,
              quantity: row.quantity,
              cutLength: row.cut_length,
              unit: row.unit,
              unitCost: row.unit_cost,
              totalCost: row.total_cost,
              allocated: row.allocated,
              consumed: row.consumed,
              stockOnHand: row.stock_on_hand,
              workstation: row.workstation,
            });
          });
          setBom(Object.values(grouped));
        }
      } else {
        setBom(bomData?.items || []);
      }
    } catch (err) {
      console.warn('Could not load BOM data:', err);
      // Try to generate preview if BOM doesn't exist
      try {
        const preview = await api.bom.generateBomPreview(id);
        setBom(preview || []);
      } catch (previewErr) {
        console.warn('Could not generate BOM preview:', previewErr);
      }
    }

    setBomLoading(false);
  };

  // Load BOM data when switching to cutsheet or bom tabs
  useEffect(() => {
    if ((activeTab === 'cutsheet' || activeTab === 'bom') && bom.length === 0 && !cutSheet && !bomLoading) {
      loadBomData();
    }
  }, [activeTab]);

  // Load QC records when switching to QC tab
  useEffect(() => {
    if (activeTab === 'qc' && qcRecords.length === 0 && !qcLoading && order?.id) {
      loadQcRecords();
    }
  }, [activeTab]);

  const loadQcRecords = async () => {
    if (!order?.id) return;
    setQcLoading(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`/api/qc-records/order/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setQcRecords(data);
      }
    } catch (err) {
      console.error('Failed to load QC records:', err);
    } finally {
      setQcLoading(false);
    }
  };

  // Check cost analytics permission
  useEffect(() => {
    checkCostPermission();
  }, []);

  const checkCostPermission = async () => {
    try {
      const result = await api.costAccounting.canView();
      setCanViewCost(result.canView);
    } catch (error) {
      console.warn('Could not check cost permission:', error);
      setCanViewCost(false);
    }
  };

  const loadCostData = async () => {
    if (!id) return;
    setCostLoading(true);
    try {
      const [analysis, comparison] = await Promise.all([
        api.costAccounting.getOrderCostAnalysis(id),
        api.costAccounting.getOrderComparison(id),
      ]);
      setCostData(analysis);
      setCostComparison(comparison);
    } catch (err) {
      console.warn('Could not load cost data:', err);
    } finally {
      setCostLoading(false);
    }
  };

  // Load cost data when switching to cost tab
  useEffect(() => {
    if (activeTab === 'cost' && !costData && !costLoading && canViewCost) {
      loadCostData();
    }
  }, [activeTab, canViewCost]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleStatusChange = async (newStatus: string, note?: string) => {
    if (!order) return;
    if (newStatus === 'cancelled') {
      setShowCancelConfirm(true);
      return;
    }
    try {
      setActionLoading(true);
      await api.orders.updateOrderStatus(order.id, newStatus as OrderStatus, note);
      await loadOrder();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!order) return;
    try {
      setActionLoading(true);
      setShowCancelConfirm(false);
      await api.orders.updateOrderStatus(order.id, 'cancelled' as OrderStatus);
      await loadOrder();
    } catch (err) {
      console.error('Failed to cancel order:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseOrder = async () => {
    if (!order) return;
    setReleaseError(null);
    // Release directly — bins are assigned at the parts pull workstation, not during release
    try {
      setActionLoading(true);
      await api.orders.releaseOrder(order.id);
      await loadOrder();
    } catch (err: any) {
      console.error('Failed to release order:', err);
      const msg = err?.message || 'Failed to release order';
      setReleaseError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRelease = async (binId?: string) => {
    if (!order) return;
    try {
      setActionLoading(true);
      setShowBinModal(false);

      // If a bin was selected, assign it first
      if (binId) {
        try {
          await api.bins.assign(binId, {
            orderId: order.id,
            workOrderId: order.id, // Work order ID may be different, using order ID for now
            orderNumber: order.orderNumber || order.order_number || '',
            assignedBy: 'admin', // Should use actual user ID
          });
        } catch (binErr) {
          console.warn('Failed to assign bin:', binErr);
          // Continue with release even if bin assignment fails
        }
      }

      // Release the order to production
      await api.orders.releaseOrder(order.id);
      await loadOrder();
    } catch (err) {
      console.error('Failed to release order:', err);
    } finally {
      setActionLoading(false);
      setSelectedBinId('');
    }
  };

  const handleAddNote = async () => {
    if (!order || !noteText.trim()) return;
    try {
      setActionLoading(true);
      await api.orders.addOrderNote(order.id, noteText);
      setNoteText('');
      setShowNoteModal(false);
      await loadOrder();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveWithoutDeposit = async () => {
    if (!order) return;
    if (!window.confirm('Are you sure you want to approve this order without requiring a deposit? This will mark the order as credit-approved.')) {
      return;
    }
    try {
      setActionLoading(true);
      await api.orders.approveWithoutDeposit(order.id);
      alert('Order approved without deposit! You can now push to production.');
      await loadOrder();
    } catch (err: any) {
      console.error('Failed to approve without deposit:', err);
      alert(`Failed to approve: ${err?.message || err?.error || 'Unknown error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!order) return;
    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setPaymentError('Please enter a valid payment amount');
      return;
    }
    try {
      setActionLoading(true);
      setPaymentError(null);
      await api.orders.recordPayment(order.id, {
        amount,
        payment_type: paymentForm.payment_type,
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference || undefined,
        notes: paymentForm.notes || undefined,
      });
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_type: 'deposit', payment_method: 'check', payment_reference: '', notes: '' });
      await loadOrder();
    } catch (err: any) {
      setPaymentError(err?.message || 'Failed to record payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!order) return;
    setEditForm({
      sidemark: order.sidemark || '',
      poNumber: order.poNumber || '',
      notes: order.notes || '',
      neededByDate: order.neededByDate || order.requestedShipDate || '',
      customerLeadName: '',
      customerLeadEmail: '',
      customerLeadPhone: '',
      customerLeadAddress: '',
    });
    // Pre-fill customer info from orderData or order
    const oi = orderData?.order || orderData || {};
    setEditForm((prev: any) => ({
      ...prev,
      customerLeadName: oi.customerName || order.customerName || '',
      customerLeadEmail: oi.customerEmail || order.customerEmail || '',
      customerLeadPhone: oi.customerPhone || order.customerPhone || '',
      customerLeadAddress: [
        oi.customerStreet || order.customerStreet || '',
        oi.customerCity || order.customerCity || '',
        [oi.customerState || order.customerState || '', oi.customerZip || order.customerZip || ''].filter(Boolean).join(' '),
      ].filter(Boolean).join(', '),
    }));
    setEditError(null);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!order) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await api.orders.adminEditOrder(order.id, editForm);
      setShowEditModal(false);
      await loadOrder();
    } catch (err: any) {
      console.error('Failed to save edit:', err);
      setEditError(err?.message || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  // Parts-order edit: open modal pre-populated from order_items.notes JSON
  const handleOpenPartsEdit = () => {
    if (!order) return;
    const items = (order as any).items || [];
    const rows = items.map((it: any) => {
      let parsed: any = {};
      if (it.notes) {
        try { parsed = typeof it.notes === 'string' ? JSON.parse(it.notes) : it.notes; } catch {}
      }
      return {
        itemId: it.id,
        itemNumber: it.itemNumber,
        name: parsed.name || '',
        description: parsed.description || '',
        color: parsed.color || '',
        quantity: Number(parsed.quantity ?? 1),
        lengthInches: parsed.lengthInches ?? null,
        exactLength: parsed.exactLength ?? false,
        sku: parsed.sku || null,
        unit: parsed.unit || 'ea',
        unitPrice: Number(it.unitPrice ?? 0),
        fabricType: parsed.fabricType ?? null,
        fabricColor: parsed.fabricColor ?? null,
        fabricOrderType: parsed.fabricOrderType ?? null,
        width: parsed.width ?? null,
        height: parsed.height ?? null,
      };
    });
    setPartsEditRows(rows);
    setPartsEditSidemark(order.sidemark || '');
    setPartsEditPo(order.poNumber || '');
    setPartsEditError(null);
    setShowPartsEditModal(true);
  };

  // Save parts edit: send full items array + sidemark/PO. Backend updates
  // order_items in place (V1 doesn't support add/remove rows) and refreshes
  // order_data + totals. Shipping is preserved as-is — user re-quotes freight
  // separately if dimensions change.
  const handleSavePartsEdit = async () => {
    if (!order) return;
    setPartsEditSaving(true);
    setPartsEditError(null);
    try {
      // Recompute subtotal client-side. Color-only changes don't change price;
      // qty/length changes do (qty × unitPrice).
      const subtotal = partsEditRows.reduce(
        (s, r) => s + (Number(r.unitPrice) || 0) * (Number(r.quantity) || 0),
        0,
      );
      const shipping = Number((order as any).shippingAmount || 0);
      const total = Math.round((subtotal + shipping) * 100) / 100;

      // Update each row's description to reflect new qty/length so the
      // dealer-portal cart display + label generation pick up the change.
      const items = partsEditRows.map((r) => {
        let description = r.description || '';
        if (r.lengthInches != null) {
          const ft = (Number(r.lengthInches) / 12).toFixed(2);
          const exact = r.exactLength ? ' (Exact)' : '';
          description = `${Number(r.lengthInches)}" (${ft} ft) x ${Number(r.quantity) || 0}${exact}`;
        }
        // Build human name from color (e.g. "U-Channel - Bronze")
        const baseName = (r.name || '').replace(/\s*-\s*[A-Za-z]+\s*$/, '').trim();
        const colorTitle = r.color ? r.color.charAt(0).toUpperCase() + r.color.slice(1) : '';
        const name = colorTitle ? `${baseName} - ${colorTitle}` : (r.name || baseName);
        return { ...r, description, name };
      });

      await api.orders.updateParts(order.id, {
        items,
        subtotal: Math.round(subtotal * 100) / 100,
        total,
        shipping,
        shippingMethod: (order as any).shippingMethod || 'will_call',
        sidemark: partsEditSidemark || null,
        poNumber: partsEditPo || null,
      });
      setShowPartsEditModal(false);
      await loadOrder();
    } catch (err: any) {
      console.error('Failed to save parts edit:', err);
      setPartsEditError(err?.message || 'Failed to save changes');
    } finally {
      setPartsEditSaving(false);
    }
  };

  const handleOpenItemEdit = (item: OrderItem) => {
    setItemEditForm({
      itemId: item.id,
      itemNumber: item.itemNumber,
      widthTop: item.widthTop ?? item.maxWidth ?? '',
      widthMiddle: item.widthMiddle ?? item.maxWidth ?? '',
      widthBottom: item.widthBottom ?? item.maxWidth ?? '',
      heightLeft: item.heightLeft ?? item.maxHeight ?? '',
      heightCenter: item.heightCenter ?? item.maxHeight ?? '',
      heightRight: item.heightRight ?? item.maxHeight ?? '',
      motorSide: item.motorSide || 'right',
      uChannel: item.uChannel || 'none',
      applyUChannelDeduction: (item as any).applyUChannelDeduction ?? false,
      boxWidthAdjustment: (item as any).boxWidthAdjustment ?? 0,
      applyBoxDeduction: (item as any).applyBoxDeduction ?? true,
      systemType: ((item as any).systemType === 'open_roll') ? 'open_roll' : 'box',
      boxWidthOverride: (item as any).boxWidthOverride ?? '',
      trackMount: item.trackMount || 'face',
      fabricType: item.fabricType || '',
      fabricColor: item.fabricColor || '',
      motorType: (item.motorType === 'keder screens' ? 'keder' : item.motorType) || '',
      systemColor: item.systemColor || '',
      brushSize: item.brushSize || item.weightBarBrush || '1inch',
      biasedCut: (item as any).biasedCut ?? false,
      biasedCutSide: (item as any).biasedCutSide || 'left',
      biasedCutInches: (item as any).biasedCutInches ?? '',
      screwCovers: (item as any).screwCovers ?? (item as any).screw_covers ?? false,
      screwCoverQty: (item as any).screwCoverQty ?? (item as any).screw_cover_qty ?? 2,
      notes: item.notes || '',
    });
    setItemEditError(null);
    setShowItemEditAdvanced(false);
    setShowItemEditModal(true);
  };

  const handleSaveItemEdit = async () => {
    if (!order) return;
    // Cut-sheet regeneration warning — if the order is already on the floor,
    // saving re-cuts the sheet the workstation is working from. Confirm first.
    const inProduction = ['in_production', 'production', 'ready_to_ship', 'partially_shipped'].includes(order.status);
    if (inProduction) {
      const ok = window.confirm(
        `Order ${order.orderNumber} is already IN PRODUCTION.\n\n` +
        `Saving these changes will REGENERATE the cut sheet for the workstation. ` +
        `Any parts already cut to the old dimensions will need to be re-cut.\n\n` +
        `Continue?`
      );
      if (!ok) return;
    }
    setItemEditSaving(true);
    setItemEditError(null);
    try {
      const { itemId, itemNumber, ...data } = itemEditForm;
      // Convert string values to numbers for measurement fields
      const payload: any = { ...data };
      ['widthTop', 'widthMiddle', 'widthBottom', 'heightLeft', 'heightCenter', 'heightRight', 'boxWidthAdjustment', 'biasedCutInches', 'screwCoverQty'].forEach(f => {
        if (payload[f] !== '' && payload[f] != null) payload[f] = Number(payload[f]);
      });
      // Bias cut: when off, clear amount so the cut sheet drops the bias
      if (!payload.biasedCut) {
        payload.biasedCutInches = 0;
        payload.biasedCutSide = null;
      } else if (payload.biasedCutInches === '') {
        payload.biasedCutInches = 0;
      }
      // Box width override: empty string means "use the formula" → send null
      payload.boxWidthOverride = (payload.boxWidthOverride === '' || payload.boxWidthOverride == null)
        ? null : Number(payload.boxWidthOverride);
      // Housing box is stored as 'box' everywhere else (dealer portal + order
      // creation); the BOM/production engine only distinguishes 'open_roll' from
      // everything-else, so keep 'box' here too instead of writing 'v_track'
      // (which left edited orders spelled differently from every other order).
      payload.systemType = payload.systemType === 'open_roll' ? 'open_roll' : 'box';
      await api.orders.adminEditItem(order.id, itemId, payload);
      setShowItemEditModal(false);
      await loadOrder();
    } catch (err: any) {
      console.error('Failed to save item edit:', err);
      setItemEditError(err?.message || 'Failed to save item changes');
    } finally {
      setItemEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading order</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <Link to="/orders" className="text-blue-600 hover:underline mt-2 inline-block">
            &larr; Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Order not found</p>
          <Link to="/orders" className="text-blue-600 hover:underline mt-2 inline-block">
            &larr; Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const availableTransitions = STATUS_TRANSITIONS[order.status] || [];
  const isDepositPaid = order.deposit_paid || order.depositPaid;
  const isCreditApproved = order.credit_approved || order.creditApproved;
  const isPaidInFull = order.paid_in_full || order.paidInFull;
  const hasStripeSession = !!(order.stripeSessionId || order.stripe_session_id);
  const isSubmittedForInvoicing = order.status === 'pending_payment' && !hasStripeSession;

  const paymentType = isPaidInFull ? 'Paid in Full' : isDepositPaid ? 'Deposit Paid' : isCreditApproved ? 'Credit Approved' : null;
  // In-flight ACH (bank payment initiated, not yet settled). Surfaced so the
  // factory can see a payment is coming even before it clears.
  const pendingAch = Number((order as any).pendingAchAmount || (order as any).pending_ach_amount || 0);
  const canEdit = !['cancelled', 'completed'].includes(order.status);

  // Parse order data for additional fields
  const orderInfo = orderData?.order || orderData || {};
  const invoiceInfo = orderData?.invoice || {};

  // Customer details
  const customerName = orderInfo.customerName || order.customerName;
  const customerPhone = orderInfo.customerPhone || order.customerPhone;
  const customerEmail = orderInfo.customerEmail || order.customerEmail;
  const customerStreet = orderInfo.customerStreet || order.customerStreet;
  const customerCity = orderInfo.customerCity || order.customerCity;
  const customerState = orderInfo.customerState || order.customerState;
  const customerZip = orderInfo.customerZip || order.customerZip;

  // Controls & Accessories. Motor is a per-item field, so fall back to the first
  // item's motor_type when the order-level snapshot doesn't carry one (hand-built
  // orders have no order_data snapshot) — and map it to a friendly label so
  // 'keder screens' reads as "Keder" instead of defaulting to Alpha.
  const motorTypeRaw = orderInfo.motorType || order.motorType
    || (Array.isArray((order as any).items) && (order as any).items[0]?.motorType)
    || (Array.isArray(orderInfo.items) && orderInfo.items[0]?.motorType) || '';
  const motorType = motorLabel(motorTypeRaw) || 'Alpha';
  const remoteType = orderInfo.remoteType || orderInfo.controllerType || order.remoteType || 'Standard';
  const remoteQty = orderInfo.remoteQty || orderInfo.controllerQty || order.remoteQty || 1;
  const wallSwitchQty = orderInfo.wallSwitchQty || order.wallSwitchQty || 0;

  // Shipping options
  const liftgateDelivery = orderInfo.liftgateDelivery || order.liftgateDelivery;
  const residentialDelivery = orderInfo.residentialDelivery || order.residentialDelivery;
  const insideDelivery = orderInfo.insideDelivery || order.insideDelivery;

  // Order info fields
  const orderedBy = orderInfo.orderedBy || orderInfo.contactName || order.orderedBy || order.contactName;
  const authorizedBy = orderInfo.authorizedBy || order.authorizedBy || orderedBy;
  const neededByDate = order.neededByDate || orderInfo.neededByDate;

  // Comments
  const comments = order.notes || orderInfo.comments || orderInfo.notes;

  const isPartsOrder = order.orderType === 'parts';

  // Parts orders store one row per physical piece — the saw needs a work order
  // per cut — so a six-box order is 108 rows. Group identical parts for display
  // and keep every cut length inside the line, so nothing is hidden.
  const groupPartsItems = (items: any[]) => {
    const toFraction = (v: number): string => {
      const whole = Math.floor(v);
      let n = Math.round((v - whole) * 16);
      if (n === 0) return `${whole}"`;
      let d = 16;
      while (n % 2 === 0) { n /= 2; d /= 2; }
      return `${whole} ${n}/${d}"`;
    };
    const groups = new Map<string, any>();
    for (const item of items || []) {
      let info: any = null;
      if (item.notes) {
        try { info = typeof item.notes === 'string' ? JSON.parse(item.notes) : item.notes; } catch {}
      }
      const price = Number(item.totalPrice ?? item.unitPrice ?? 0) || 0;
      const qty = Number(info?.quantity) || 1;
      // Include length in the key so different cut lengths show as SEPARATE line
      // items (e.g. U-Channel 144" ×4 and 107" ×4), not one lumped "× 8" with a
      // confusing "Cuts: 144, 107". Fabric (no lengthInches) groups as before.
      const key = [info?.sku || info?.name || item.productType || 'part', info?.color || '', info?.fabricType || '', info?.lengthInches ?? ''].join('|');
      const existing = groups.get(key);
      if (existing) {
        existing.quantity += qty;
        existing.lineTotal += price;
        if (price !== existing.firstPrice) existing.mixedPricing = true;
        if (info?.lengthInches) existing.lengths.push(Number(info.lengthInches));
      } else {
        groups.set(key, {
          id: item.id, info, quantity: qty, lineTotal: price,
          firstPrice: price, mixedPricing: false,
          lengths: info?.lengthInches ? [Number(info.lengthInches)] : [],
        });
      }
    }
    return [...groups.values()].map(g => ({
      ...g,
      lengthLabel: g.lengths.length === 0 ? null
        : g.lengths.length === 1 ? `Cut ${toFraction(g.lengths[0])}`
        : `Cuts: ${g.lengths.map(toFraction).join(', ')}`,
    }));
  };


  const handleOpenLabelModal = async () => {
    setShowLabelModal(true);
    setLabelResults([]);
    try {
      const printers = await api.labels.getPrinters();
      setLabelPrinters(printers);
      if (printers.length > 0) {
        const defaultPrinter = printers.find((p: any) => p.isDefault) || printers[0];
        setSelectedPrinterId(defaultPrinter.id);
      }
    } catch (err) {
      console.error('Failed to load printers:', err);
    }
  };

  const handlePrintLabels = async () => {
    if (!order?.id) return;
    setLabelPrinting(true);
    setLabelResults([]);
    try {
      const labels = await api.labels.queueOrderLabels(order.id, labelType, labelCopies);
      if (selectedPrinterId) {
        const results: { id: string; status: string; message?: string }[] = [];
        for (const label of labels) {
          try {
            const res = await api.labels.printDirect(label.id, selectedPrinterId);
            results.push({ id: label.id, status: 'printed', message: res.message });
          } catch (err: any) {
            results.push({ id: label.id, status: 'error', message: err.message });
          }
        }
        setLabelResults(results);
      } else {
        setLabelResults(labels.map((l: any) => ({ id: l.id, status: 'queued', message: 'Queued (no printer selected)' })));
      }
    } catch (err: any) {
      setLabelResults([{ id: '', status: 'error', message: err.message }]);
    } finally {
      setLabelPrinting(false);
    }
  };

  const handlePrintOrder = () => {
    if (!order) return;
    const items = order.items || [];
    const accessories = order.accessories || [];
    const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

    const calcWidths = (item: any) => {
      const isInside = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(item.trackMount || '');
      const wVals = [item.widthTop, item.widthMiddle, item.widthBottom].filter((v: any) => v != null && v > 0);
      const w = wVals.length > 0 ? (isInside ? Math.min(...wVals) : Math.max(...wVals)) : (item.maxWidth || 0);
      // Box width uses min (w) as base + dealer adjustment (carries top-vs-min diff, capped 0.75)
      let tmAdj = 0;
      const tm = item.trackMount || '';
      if (tm === 'wall') tmAdj = 6;
      else if (tm === 'inside_face_left' || tm === 'inside_face_right') tmAdj = 3;
      let uDed = 0;
      if (item.applyUChannelDeduction && item.uChannel && item.uChannel !== 'none') {
        uDed = item.uChannel === 'two_sides' ? 0.75 : 0.375;
      }
      const boxDed = (item.applyBoxDeduction !== false) ? 0.25 : 0;
      const dBoxAdj = item.boxWidthAdjustment || 0;
      return {
        selectedWidth: w,
        systemWidth: (w + tmAdj - uDed).toFixed(3),
        boxWidth: (w + tmAdj - boxDed + dBoxAdj).toFixed(3),
      };
    };

    const orderData = order.orderData || {};
    const orderedBy = orderData.orderedBy || order.contactName || '';
    const sidemark = order.sidemark || '';

    const itemRows = items.map((item: any) => {
      const wt = item.widthTop || item.maxWidth || 0;
      const wm = item.widthMiddle || item.maxWidth || 0;
      const wb = item.widthBottom || item.maxWidth || 0;
      const hl = item.heightLeft || item.maxHeight || 0;
      const hc = item.heightCenter || item.maxHeight || 0;
      const hr = item.heightRight || item.maxHeight || 0;
      const cw = calcWidths(item);
      return `
        <div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;page-break-inside:avoid;">
          <div style="background:#f9fafb;padding:10px 14px;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:600;">Unit #${item.itemNumber}</span>
            <span style="font-weight:500;">${fmt(item.totalPrice || item.unitPrice || 0)}</span>
          </div>
          <div style="padding:14px;">
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              <tr>
                <td style="padding:3px 0;color:#6b7280;width:130px;">Width:</td>
                <td style="padding:3px 0;">T: ${wt}" | M: ${wm}" | B: ${wb}"</td>
                <td style="padding:3px 0;color:#6b7280;width:130px;">Height:</td>
                <td style="padding:3px 0;">L: ${hl}" | C: ${hc}" | R: ${hr}"</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">System Width:</td>
                <td style="padding:3px 0;font-weight:600;color:#1d4ed8;">${cw.systemWidth}"</td>
                <td style="padding:3px 0;color:#6b7280;">Box Width:</td>
                <td style="padding:3px 0;font-weight:600;color:#4338ca;">${cw.boxWidth}"</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Fabric:</td>
                <td style="padding:3px 0;">${formatFabricType(item.fabricType)} — ${item.fabricColor || '-'}</td>
                <td style="padding:3px 0;color:#6b7280;">Frame Color:</td>
                <td style="padding:3px 0;">${item.systemColor || order.systemColor || 'Black'}</td>
              </tr>
              ${item.componentColors && Object.keys(item.componentColors).length > 0 ? `
              <tr><td colspan="4" style="padding:4px 0;">
                <div style="background:#fff7ed;border:1px solid #fdba74;padding:4px 8px;border-radius:4px;font-size:12px;">
                  <strong style="color:#c2410c;">Mixed Colors:</strong>
                  ${item.componentColors.housingColor ? ` Box=${item.componentColors.housingColor === 'custom' ? `Custom (${item.componentColors.housingRalNumber || ''})` : item.componentColors.housingColor}` : ''}
                  ${item.componentColors.leftTrackColor ? ` · L.Track=${item.componentColors.leftTrackColor === 'custom' ? `Custom (${item.componentColors.leftTrackRalNumber || ''})` : item.componentColors.leftTrackColor}` : ''}
                  ${item.componentColors.rightTrackColor ? ` · R.Track=${item.componentColors.rightTrackColor === 'custom' ? `Custom (${item.componentColors.rightTrackRalNumber || ''})` : item.componentColors.rightTrackColor}` : ''}
                  ${item.componentColors.weightBarColor ? ` · WBar=${item.componentColors.weightBarColor === 'custom' ? `Custom (${item.componentColors.weightBarRalNumber || ''})` : item.componentColors.weightBarColor}` : ''}
                  ${item.componentColors.leftUChannelColor ? ` · L.UCh=${item.componentColors.leftUChannelColor === 'custom' ? `Custom (${item.componentColors.leftUChannelRalNumber || ''})` : item.componentColors.leftUChannelColor}` : ''}
                  ${item.componentColors.rightUChannelColor ? ` · R.UCh=${item.componentColors.rightUChannelColor === 'custom' ? `Custom (${item.componentColors.rightUChannelRalNumber || ''})` : item.componentColors.rightUChannelColor}` : ''}
                </div>
              </td></tr>` : ''}
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Installation:</td>
                <td style="padding:3px 0;">${formatInstallationType(item.installationType)}</td>
                <td style="padding:3px 0;color:#6b7280;">Track Mount:</td>
                <td style="padding:3px 0;">${formatTrackMount(item.trackMount)}</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Motor:</td>
                <td style="padding:3px 0;">${motorLabel(item.motorType) || 'Alpha'} — ${(item.motorSide || 'Right').replace(/\b\w/g, (c: string) => c.toUpperCase())}</td>
                <td style="padding:3px 0;color:#6b7280;">U-Channel:</td>
                <td style="padding:3px 0;">${formatUChannel(item.uChannel)}</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Brush Size:</td>
                <td style="padding:3px 0;">${formatBrushSize(item.brushSize || item.weightBarBrush)}</td>
                <td style="padding:3px 0;color:#6b7280;">Biased Cut:</td>
                <td style="padding:3px 0;">${item.biasedCut ? 'Yes (' + (item.biasedCutInches || 0) + '"' + (item.biasedCutSide ? ', ' + (item.biasedCutSide === 'left' ? 'right' : 'left') + ' side shorter' : '') + ')' : 'No'}</td>
              </tr>
            </table>
          </div>
        </div>`;
    }).join('');

    const accRows = accessories.length > 0 ? accessories.map((acc: any) =>
      `<tr>
        <td style="padding:4px 8px;border-bottom:1px solid #f3f4f6;">${acc.description || acc.accessory_type}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #f3f4f6;text-align:center;">${acc.quantity || 1}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #f3f4f6;text-align:right;">${fmt(parseFloat(acc.total_price || acc.totalPrice || 0))}</td>
      </tr>`
    ).join('') : '';

    const shipTo = order.shipToAddress || order.ship_to_address;
    const shipAddr = shipTo ? (typeof shipTo === 'string' ? JSON.parse(shipTo) : shipTo) : null;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head>
      <title>Order ${order.orderNumber}</title>
      <style>
        * { margin:0;padding:0;box-sizing:border-box; }
        body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:850px;margin:0 auto;padding:30px;color:#1f2937;font-size:14px; }
        @media print { body { padding:15px; } }
      </style>
    </head><body>
      <div style="display:flex;justify-content:space-between;border-bottom:3px solid #1f2937;padding-bottom:16px;margin-bottom:20px;">
        <div>
          <h1 style="font-size:24px;font-weight:800;">ORDER ${order.orderNumber}</h1>
          ${sidemark ? `<p style="color:#6b7280;margin-top:2px;">Sidemark: ${sidemark}</p>` : ''}
          ${order.poNumber ? `<p style="color:#6b7280;">PO: ${order.poNumber}</p>` : ''}
        </div>
        <div style="text-align:right;">
          <p style="font-weight:700;">Keder Screens</p>
          <p style="color:#6b7280;margin-top:4px;">Date: ${order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</p>
          <p style="color:#6b7280;">Status: ${(order.status || '').replace(/_/g, ' ').toUpperCase()}</p>
        </div>
      </div>

      <div style="display:flex;justify-content:space-between;margin-bottom:24px;">
        <div>
          <p style="font-weight:600;margin-bottom:2px;">Dealer</p>
          <p style="font-weight:500;">${order.companyName || ''}</p>
          ${orderedBy ? `<p style="color:#6b7280;">${orderedBy}</p>` : ''}
          ${order.contactEmail ? `<p style="color:#6b7280;">${order.contactEmail}</p>` : ''}
        </div>
        ${shipAddr ? `<div>
          <p style="font-weight:600;margin-bottom:2px;">Ship To</p>
          ${shipAddr.name || shipAddr.attention ? `<p style="color:#6b7280;">${shipAddr.name || shipAddr.attention}</p>` : ''}
          ${shipAddr.street || shipAddr.address ? `<p style="color:#6b7280;">${shipAddr.street || shipAddr.address}</p>` : ''}
          <p style="color:#6b7280;">${shipAddr.city || ''}${shipAddr.state ? ', ' + shipAddr.state : ''} ${shipAddr.zip || shipAddr.zipCode || ''}</p>
        </div>` : ''}
        <div>
          <p style="font-weight:600;margin-bottom:2px;">Shipping</p>
          <p style="color:#6b7280;">${formatShippingMethod(order.shippingMethod)}</p>
        </div>
      </div>

      <h2 style="font-size:16px;font-weight:700;margin-bottom:12px;border-bottom:2px solid #e5e7eb;padding-bottom:6px;">Screens (${items.length})</h2>
      ${itemRows}

      ${accessories.length > 0 ? `
        <h2 style="font-size:16px;font-weight:700;margin-bottom:8px;margin-top:20px;border-bottom:2px solid #e5e7eb;padding-bottom:6px;">Accessories</h2>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px;">
          <thead><tr style="background:#f9fafb;">
            <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e5e7eb;">Description</th>
            <th style="padding:6px 8px;text-align:center;border-bottom:1px solid #e5e7eb;width:60px;">Qty</th>
            <th style="padding:6px 8px;text-align:right;border-bottom:1px solid #e5e7eb;width:100px;">Amount</th>
          </tr></thead>
          <tbody>${accRows}</tbody>
        </table>
      ` : ''}

      <div style="margin-top:24px;margin-left:auto;width:280px;">
        <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>
        ${order.discountAmount > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#059669;"><span>Discount (${order.discountRate || 0}%)</span><span>-${fmt(order.discountAmount)}</span></div>` : ''}
        ${(order.packagingAmount || 0) > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#6b7280;"><span>Packaging</span><span>${fmt(order.packagingAmount)}</span></div>` : ''}
        ${(order.shippingAmount || 0) > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;color:#6b7280;"><span>Shipping</span><span>${fmt(order.shippingAmount)}</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #1f2937;font-weight:800;font-size:16px;"><span>Total</span><span>${fmt(order.totalAmount)}</span></div>
      </div>

      ${order.notes ? `<div style="margin-top:20px;padding:12px;background:#f9fafb;border-radius:6px;"><p style="font-weight:600;margin-bottom:2px;">Notes</p><p style="color:#6b7280;white-space:pre-wrap;">${order.notes}</p></div>` : ''}
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // Print Production Sheet — same as Print Order but with NO prices
  const handlePrintProductionSheet = () => {
    if (!order) return;
    const items = order.items || [];
    const accessories = order.accessories || [];

    const calcWidths = (item: any) => {
      const isInside = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(item.trackMount || '');
      const wVals = [item.widthTop, item.widthMiddle, item.widthBottom].filter((v: any) => v != null && v > 0);
      const w = wVals.length > 0 ? (isInside ? Math.min(...wVals) : Math.max(...wVals)) : (item.maxWidth || 0);
      let tmAdj = 0;
      const tm = item.trackMount || '';
      if (tm === 'wall') tmAdj = 6;
      else if (tm === 'inside_face_left' || tm === 'inside_face_right') tmAdj = 3;
      let uDed = 0;
      if (item.applyUChannelDeduction && item.uChannel && item.uChannel !== 'none') {
        uDed = item.uChannel === 'two_sides' ? 0.75 : 0.375;
      }
      const boxDed = (item.applyBoxDeduction !== false) ? 0.25 : 0;
      const dBoxAdj = item.boxWidthAdjustment || 0;
      return {
        selectedWidth: w,
        systemWidth: (w + tmAdj - uDed).toFixed(3),
        boxWidth: (w + tmAdj - boxDed + dBoxAdj).toFixed(3),
      };
    };

    const orderData = order.orderData || {};
    const orderedBy = orderData.orderedBy || order.contactName || '';
    const sidemark = order.sidemark || '';

    const itemRows = items.map((item: any) => {
      const wt = item.widthTop || item.maxWidth || 0;
      const wm = item.widthMiddle || item.maxWidth || 0;
      const wb = item.widthBottom || item.maxWidth || 0;
      const hl = item.heightLeft || item.maxHeight || 0;
      const hc = item.heightCenter || item.maxHeight || 0;
      const hr = item.heightRight || item.maxHeight || 0;
      const cw = calcWidths(item);
      return `
        <div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;page-break-inside:avoid;">
          <div style="background:#f9fafb;padding:10px 14px;border-bottom:1px solid #e5e7eb;">
            <span style="font-weight:600;">Unit #${item.itemNumber}</span>
          </div>
          <div style="padding:14px;">
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              <tr>
                <td style="padding:3px 0;color:#6b7280;width:130px;">Width:</td>
                <td style="padding:3px 0;">T: ${wt}" | M: ${wm}" | B: ${wb}"</td>
                <td style="padding:3px 0;color:#6b7280;width:130px;">Height:</td>
                <td style="padding:3px 0;">L: ${hl}" | C: ${hc}" | R: ${hr}"</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">System Width:</td>
                <td style="padding:3px 0;font-weight:600;color:#1d4ed8;">${cw.systemWidth}"</td>
                <td style="padding:3px 0;color:#6b7280;">Box Width:</td>
                <td style="padding:3px 0;font-weight:600;color:#4338ca;">${cw.boxWidth}"</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Fabric:</td>
                <td style="padding:3px 0;">${formatFabricType(item.fabricType)} — ${item.fabricColor || '-'}</td>
                <td style="padding:3px 0;color:#6b7280;">Frame Color:</td>
                <td style="padding:3px 0;">${item.systemColor || order.systemColor || 'Black'}</td>
              </tr>
              ${item.componentColors && Object.keys(item.componentColors).length > 0 ? `
              <tr><td colspan="4" style="padding:4px 0;">
                <div style="background:#fff7ed;border:1px solid #fdba74;padding:4px 8px;border-radius:4px;font-size:12px;">
                  <strong style="color:#c2410c;">Mixed Colors:</strong>
                  ${item.componentColors.housingColor ? ` Box=${item.componentColors.housingColor === 'custom' ? `Custom (${item.componentColors.housingRalNumber || ''})` : item.componentColors.housingColor}` : ''}
                  ${item.componentColors.leftTrackColor ? ` · L.Track=${item.componentColors.leftTrackColor === 'custom' ? `Custom (${item.componentColors.leftTrackRalNumber || ''})` : item.componentColors.leftTrackColor}` : ''}
                  ${item.componentColors.rightTrackColor ? ` · R.Track=${item.componentColors.rightTrackColor === 'custom' ? `Custom (${item.componentColors.rightTrackRalNumber || ''})` : item.componentColors.rightTrackColor}` : ''}
                  ${item.componentColors.weightBarColor ? ` · WBar=${item.componentColors.weightBarColor === 'custom' ? `Custom (${item.componentColors.weightBarRalNumber || ''})` : item.componentColors.weightBarColor}` : ''}
                  ${item.componentColors.leftUChannelColor ? ` · L.UCh=${item.componentColors.leftUChannelColor === 'custom' ? `Custom (${item.componentColors.leftUChannelRalNumber || ''})` : item.componentColors.leftUChannelColor}` : ''}
                  ${item.componentColors.rightUChannelColor ? ` · R.UCh=${item.componentColors.rightUChannelColor === 'custom' ? `Custom (${item.componentColors.rightUChannelRalNumber || ''})` : item.componentColors.rightUChannelColor}` : ''}
                </div>
              </td></tr>` : ''}
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Installation:</td>
                <td style="padding:3px 0;">${formatInstallationType(item.installationType)}</td>
                <td style="padding:3px 0;color:#6b7280;">Track Mount:</td>
                <td style="padding:3px 0;">${formatTrackMount(item.trackMount)}</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Motor:</td>
                <td style="padding:3px 0;">${motorLabel(item.motorType) || 'Alpha'} — ${(item.motorSide || 'Right').replace(/\b\w/g, (c: string) => c.toUpperCase())}</td>
                <td style="padding:3px 0;color:#6b7280;">U-Channel:</td>
                <td style="padding:3px 0;">${formatUChannel(item.uChannel)}</td>
              </tr>
              <tr>
                <td style="padding:3px 0;color:#6b7280;">Brush Size:</td>
                <td style="padding:3px 0;">${formatBrushSize(item.brushSize || item.weightBarBrush)}</td>
                <td style="padding:3px 0;color:#6b7280;">Biased Cut:</td>
                <td style="padding:3px 0;">${item.biasedCut ? 'Yes (' + (item.biasedCutInches || 0) + '"' + (item.biasedCutSide ? ', ' + (item.biasedCutSide === 'left' ? 'right' : 'left') + ' side shorter' : '') + ')' : 'No'}</td>
              </tr>
            </table>
          </div>
        </div>`;
    }).join('');

    const accRows = accessories.length > 0 ? accessories.map((acc: any) =>
      `<tr>
        <td style="padding:4px 8px;border-bottom:1px solid #f3f4f6;">${acc.description || acc.accessory_type}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #f3f4f6;text-align:center;">${acc.quantity || 1}</td>
      </tr>`
    ).join('') : '';

    const shipTo = order.shipToAddress || order.ship_to_address;
    const shipAddr = shipTo ? (typeof shipTo === 'string' ? JSON.parse(shipTo) : shipTo) : null;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head>
      <title>Production Sheet - ${order.orderNumber}</title>
      <style>
        * { margin:0;padding:0;box-sizing:border-box; }
        body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:850px;margin:0 auto;padding:30px;color:#1f2937;font-size:14px; }
        @media print { body { padding:15px; } }
      </style>
    </head><body>
      <div style="display:flex;justify-content:space-between;border-bottom:3px solid #1f2937;padding-bottom:16px;margin-bottom:20px;">
        <div>
          <h1 style="font-size:24px;font-weight:800;">PRODUCTION SHEET — ${order.orderNumber}</h1>
          ${sidemark ? `<p style="color:#6b7280;margin-top:2px;">Sidemark: ${sidemark}</p>` : ''}
          ${order.poNumber ? `<p style="color:#6b7280;">PO: ${order.poNumber}</p>` : ''}
        </div>
        <div style="text-align:right;">
          <p style="font-weight:700;">Keder Screens</p>
          <p style="color:#6b7280;margin-top:4px;">Date: ${order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</p>
          <p style="color:#6b7280;">Status: ${(order.status || '').replace(/_/g, ' ').toUpperCase()}</p>
        </div>
      </div>

      <div style="display:flex;justify-content:space-between;margin-bottom:24px;">
        <div>
          <p style="font-weight:600;margin-bottom:2px;">Dealer</p>
          <p style="font-weight:500;">${order.companyName || ''}</p>
          ${orderedBy ? `<p style="color:#6b7280;">${orderedBy}</p>` : ''}
        </div>
        ${shipAddr ? `<div>
          <p style="font-weight:600;margin-bottom:2px;">Ship To</p>
          ${shipAddr.name || shipAddr.attention ? `<p style="color:#6b7280;">${shipAddr.name || shipAddr.attention}</p>` : ''}
          ${shipAddr.street || shipAddr.address ? `<p style="color:#6b7280;">${shipAddr.street || shipAddr.address}</p>` : ''}
          <p style="color:#6b7280;">${shipAddr.city || ''}${shipAddr.state ? ', ' + shipAddr.state : ''} ${shipAddr.zip || shipAddr.zipCode || ''}</p>
        </div>` : ''}
        <div>
          <p style="font-weight:600;margin-bottom:2px;">Shipping</p>
          <p style="color:#6b7280;">${formatShippingMethod(order.shippingMethod)}</p>
        </div>
      </div>

      <h2 style="font-size:16px;font-weight:700;margin-bottom:12px;border-bottom:2px solid #e5e7eb;padding-bottom:6px;">Screens (${items.length})</h2>
      ${itemRows}

      ${accessories.length > 0 ? `
        <h2 style="font-size:16px;font-weight:700;margin-bottom:8px;margin-top:20px;border-bottom:2px solid #e5e7eb;padding-bottom:6px;">Accessories</h2>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px;">
          <thead><tr style="background:#f9fafb;">
            <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e5e7eb;">Description</th>
            <th style="padding:6px 8px;text-align:center;border-bottom:1px solid #e5e7eb;width:60px;">Qty</th>
          </tr></thead>
          <tbody>${accRows}</tbody>
        </table>
      ` : ''}

      ${order.notes ? `<div style="margin-top:20px;padding:12px;background:#f9fafb;border-radius:6px;"><p style="font-weight:600;margin-bottom:2px;">Notes</p><p style="color:#6b7280;white-space:pre-wrap;">${order.notes}</p></div>` : ''}
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // Print Cut Sheet
  const handlePrintCutSheet = async () => {
    if (!order) return;

    // Load cut sheet data if not already loaded
    let csData = cutSheet;
    if (!csData) {
      try {
        const data = await api.bom.getCutSheet(order.id);
        if (Array.isArray(data)) {
          csData = { items: data, generatedAt: new Date().toISOString() };
        } else {
          csData = data;
        }
      } catch {
        alert('Failed to load cut sheet data. Ensure the order has been released to production.');
        return;
      }
    }

    if (!csData?.items?.length) {
      alert('No cut sheet data available. Cut sheet is generated when the order is pushed to production.');
      return;
    }

    const items = order.items || [];

    const unitSections = csData.items.map((cs: any, index: number) => {
      const itemNum = cs.itemNumber || index + 1;
      const orderItem = items.find((i: any) => i.itemNumber === itemNum);
      const dim = cs.calculatedDimensions || {};
      const cuts = cs.cutSizes || {};
      const boxW = ((dim.widthSelected || 0) + (dim.boxWidthAdjustment || 0) - (dim.boxDeduction || 0) + (dim.dealerBoxAdjustment || 0)).toFixed(3);

      const cutRows = [
        cuts.housingMain != null ? `<div style="background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Housing Main</div><div style="font-size:18px;font-weight:700;">${cuts.housingMain.toFixed(3)}"</div></div>` : '',
        cuts.housingCover != null ? `<div style="background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Housing Cover</div><div style="font-size:18px;font-weight:700;">${cuts.housingCover.toFixed(3)}"</div></div>` : '',
        cuts.rollerTube != null ? `<div style="background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Roller Tube</div><div style="font-size:18px;font-weight:700;">${cuts.rollerTube.toFixed(3)}"</div></div>` : '',
        cuts.vTrack != null ? `<div style="background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#6b7280;">V-Track</div><div style="font-size:18px;font-weight:700;">${cuts.vTrack.toFixed(3)}"</div></div>` : '',
        cuts.weightBar != null ? `<div style="background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Weight Bar</div><div style="font-size:18px;font-weight:700;">${cuts.weightBar.toFixed(3)}"</div></div>` : '',
        cuts.uChannel != null ? `<div style="background:#fff;border:1px solid #93c5fd;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#2563eb;">U-Channel</div><div style="font-size:18px;font-weight:700;">${cuts.uChannel.toFixed(3)}"</div></div>` : '',
        cuts.fabricWidth != null ? `<div style="background:#fff;border:1px solid #86efac;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#16a34a;">Fabric Width</div><div style="font-size:18px;font-weight:700;">${cuts.fabricWidth.toFixed(3)}"</div></div>` : '',
        cuts.fabricHeight != null ? `<div style="background:#fff;border:1px solid #86efac;border-radius:6px;padding:10px;text-align:center;"><div style="font-size:11px;color:#16a34a;">Fabric Height</div><div style="font-size:18px;font-weight:700;">${cuts.fabricHeight.toFixed(3)}"</div></div>` : '',
      ].filter(Boolean).join('');

      // Jamb-mount and recessed-pocket tracks get their holes drilled here but
      // the springs are installed in the FIELD, not at the bench. Flag it on the
      // cut sheet so the shop drills the holes and skips the bench spring install.
      const fieldInstallSprings = (() => {
        const tm = String(orderItem?.trackMount || '').toLowerCase();
        const it = String(orderItem?.installationType || '').toLowerCase();
        return tm === 'jamb' || it.includes('recessed');
      })();
      const springHtml = cs.springHoles ? `
        <div style="background:#f5f3ff;border:1px solid #c4b5fd;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="font-size:12px;font-weight:700;color:#6d28d9;text-transform:uppercase;margin-bottom:8px;">Spring Hole Positions</div>
          <div style="display:flex;gap:16px;margin-bottom:8px;">
            <span style="font-size:13px;"><strong>${cs.springHoles.holes || cs.springHoles.count || 0}</strong> holes/track</span>
            <span style="font-size:13px;">Spacing: <strong>${(cs.springHoles.spacing || 0).toFixed(2)}"</strong></span>
            <span style="font-size:13px;">Springs: <strong>${cs.springHoles.springs ?? ((cs.springHoles.holes || 0) * 4)}</strong> per unit</span>
          </div>
          ${cs.springHoles.positions?.length ? `<div style="display:flex;flex-wrap:wrap;gap:6px;">${cs.springHoles.positions.map((p: number) => `<span style="padding:3px 10px;background:#ede9fe;color:#6d28d9;border-radius:12px;font-size:12px;font-family:monospace;">${p.toFixed(2)}"</span>`).join('')}</div>` : ''}
          ${fieldInstallSprings ? `<div style="margin-top:10px;padding:8px 10px;background:#fff7ed;border:1px solid #fdba74;border-radius:6px;font-size:12px;font-weight:700;color:#c2410c;">⚠ SPRINGS FIELD-INSTALLED — drill holes here, do NOT install springs at bench (${String(orderItem?.trackMount || '').toLowerCase() === 'jamb' ? 'jamb' : 'recessed'} mount)</div>` : ''}
        </div>
      ` : '';

      // Biased cut. Show BOTH sides explicitly — the stored `biased_cut_side` is
      // the LONGER side, and that has been read backwards before, so naming only
      // one side invites the same mistake on the floor. Until now the cut sheet
      // showed no bias at all: a biased unit printed identically to a square one.
      const biasHtml = cs.biasedCut?.enabled ? `
        <div style="background:#fff1f2;border:1px solid #fda4af;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="font-size:12px;font-weight:700;color:#be123c;text-transform:uppercase;margin-bottom:6px;">&#9888; Biased Cut</div>
          <div style="display:flex;gap:16px;font-size:13px;align-items:center;">
            <span><strong style="text-transform:uppercase;">${cs.biasedCut.shorterSide || '?'}</strong> side <strong>${cs.biasedCut.inches}"</strong> SHORTER</span>
            <span style="color:#9f1239;">(${(cs.biasedCut.longerSide || '?').toUpperCase()} side is the long side)</span>
          </div>
          <div style="margin-top:6px;font-size:12px;color:#9f1239;">Fabric is cut on an angle — confirm orientation before cutting.</div>
        </div>
      ` : '';

      const seamHtml = cs.seamRequired ? `
        <div style="background:#fefce8;border:1px solid #fde047;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="font-size:12px;font-weight:700;color:#a16207;text-transform:uppercase;margin-bottom:6px;">⚠ Seam Required</div>
          ${cs.seamDetails ? `<div style="display:flex;gap:16px;font-size:13px;">
            <span>Panel 1: <strong>${cs.seamDetails.panel1Height?.toFixed(2) || '-'}"</strong></span>
            <span>Panel 2: <strong>${cs.seamDetails.panel2Height?.toFixed(2) || '-'}"</strong></span>
            <span>Total: <strong>${cs.seamDetails.totalHeight?.toFixed(2) || '-'}"</strong></span>
          </div>` : ''}
        </div>
      ` : '';

      const dropHtml = cs.dropAllocations?.length ? `
        <div style="background:#ecfdf5;border:1px solid #6ee7b7;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="font-size:12px;font-weight:700;color:#047857;text-transform:uppercase;margin-bottom:8px;">Drop Allocations</div>
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead><tr style="border-bottom:1px solid #a7f3d0;">
              <th style="text-align:left;padding:4px;">Drop ID</th>
              <th style="text-align:left;padding:4px;">Component</th>
              <th style="text-align:left;padding:4px;">Bin</th>
              <th style="text-align:right;padding:4px;">Available</th>
              <th style="text-align:right;padding:4px;">Cut To</th>
            </tr></thead>
            <tbody>${cs.dropAllocations.map((d: any) => `<tr style="border-bottom:1px solid #d1fae5;">
              <td style="padding:4px;font-weight:600;color:#047857;">${d.dropId}</td>
              <td style="padding:4px;">${(d.componentType || '').replace(/_/g, ' ')}</td>
              <td style="padding:4px;">${d.dropBinLocation || 'N/A'}</td>
              <td style="padding:4px;text-align:right;">${d.dropLength}"</td>
              <td style="padding:4px;text-align:right;">${d.cutLength}"</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>
      ` : '';

      const warningHtml = cs.warnings?.length ? `
        <div style="background:#fef2f2;border:1px solid #fca5a5;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="font-size:12px;font-weight:700;color:#b91c1c;text-transform:uppercase;margin-bottom:6px;">Warnings</div>
          <ul style="margin:0;padding-left:18px;font-size:13px;color:#dc2626;">${cs.warnings.map((w: string) => `<li>${w}</li>`).join('')}</ul>
        </div>
      ` : '';

      return `
        <div style="border:1px solid #d1d5db;border-radius:10px;margin-bottom:20px;page-break-inside:avoid;">
          <div style="background:#f3f4f6;padding:10px 16px;border-bottom:1px solid #d1d5db;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:700;font-size:15px;">Unit #${itemNum}</span>
            <span style="font-size:13px;color:#6b7280;">
              ${orderItem ? `${formatFabricType(orderItem.fabricType)} — ${orderItem.fabricColor || ''} | ${orderItem.systemColor || 'Black'} | ${formatTrackMount(orderItem.trackMount)}` : ''}
            </span>
          </div>
          <div style="padding:16px;">
            <div style="background:#eff6ff;border:1px solid #93c5fd;border-radius:8px;padding:12px;margin-bottom:12px;">
              <div style="font-size:12px;font-weight:700;color:#1d4ed8;text-transform:uppercase;margin-bottom:8px;">System Dimensions</div>
              <div style="display:flex;gap:24px;flex-wrap:wrap;">
                <div><span style="color:#6b7280;font-size:12px;">System Width:</span> <strong style="color:#1d4ed8;font-size:16px;">${dim.systemWidth?.toFixed(3) || '-'}"</strong></div>
                <div><span style="color:#6b7280;font-size:12px;">System Height:</span> <strong style="font-size:16px;">${dim.systemHeight?.toFixed(3) || '-'}"</strong></div>
                <div><span style="color:#6b7280;font-size:12px;">Box Width:</span> <strong style="color:#4338ca;font-size:16px;">${boxW}"</strong></div>
                ${dim.uChannelDeduction > 0 ? `<div><span style="color:#6b7280;font-size:12px;">U-Ch Ded:</span> <strong style="color:#ea580c;">-${dim.uChannelDeduction}"</strong></div>` : ''}
                ${dim.dealerBoxAdjustment ? `<div><span style="color:#6b7280;font-size:12px;">Dealer Box Adj:</span> <strong style="color:#ea580c;">${dim.dealerBoxAdjustment > 0 ? '+' : ''}${dim.dealerBoxAdjustment}"</strong></div>` : ''}
              </div>
            </div>

            <div style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:12px;">
              <div style="font-size:12px;font-weight:700;color:#16a34a;text-transform:uppercase;margin-bottom:8px;">Cut Sizes</div>
              <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;">
                ${cutRows}
              </div>
            </div>

            ${dropHtml}
            ${springHtml}
            ${biasHtml}
            ${seamHtml}
            ${warningHtml}
          </div>
        </div>`;
    }).join('');

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head>
      <title>Cut Sheet - ${order.orderNumber}</title>
      <style>
        * { margin:0;padding:0;box-sizing:border-box; }
        body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:850px;margin:0 auto;padding:30px;color:#1f2937;font-size:14px; }
        @media print { body { padding:15px; } .no-break { page-break-inside:avoid; } }
      </style>
    </head><body>
      <div style="display:flex;justify-content:space-between;border-bottom:3px solid #16a34a;padding-bottom:16px;margin-bottom:20px;">
        <div>
          <h1 style="font-size:24px;font-weight:800;">CUT SHEET — ${order.orderNumber}</h1>
          ${order.sidemark ? `<p style="color:#6b7280;margin-top:2px;">Sidemark: ${order.sidemark}</p>` : ''}
        </div>
        <div style="text-align:right;">
          <p style="font-weight:700;">Keder Screens</p>
          <p style="color:#6b7280;margin-top:4px;">${csData.items.length} unit${csData.items.length !== 1 ? 's' : ''}</p>
          <p style="color:#6b7280;">Generated: ${new Date().toLocaleDateString()}</p>
        </div>
      </div>

      ${unitSections}
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // RUSH toggle handlers — admin can flag or un-flag, optionally waive surcharge
  const isRush = (order as any).isRush === true || (order as any).is_rush === true;
  const rushSurchargeAmount = parseFloat((order as any).rushSurchargeAmount || (order as any).rush_surcharge_amount || '0') || 0;
  const rushWaived = (order as any).rushSurchargeWaived === true || (order as any).rush_surcharge_waived === true;
  const toggleRush = async () => {
    const next = !isRush;
    const msg = next
      ? 'Mark this order as RUSH? A +25% surcharge of the dealer-net screen price will be added to the invoice.'
      : 'Un-flag rush? The 25% surcharge will be removed from the invoice.';
    if (!confirm(msg)) return;
    try {
      await api.orders.updateRush(id!, { isRush: next });
      window.location.reload();
    } catch (e: any) {
      alert(`Failed: ${e?.message || e}`);
    }
  };
  const toggleWaiver = async () => {
    if (!confirm(rushWaived
      ? 'Restore the 25% rush surcharge on this order?'
      : 'Waive the 25% rush surcharge? Rush priority stays in place; only the charge is removed.')) return;
    try {
      await api.orders.updateRush(id!, { rushSurchargeWaived: !rushWaived });
      window.location.reload();
    } catch (e: any) {
      alert(`Failed: ${e?.message || e}`);
    }
  };

  return (
    <div>
      {isRush && (
        <div className="mb-4 px-4 py-3 bg-red-600 text-white rounded-lg flex items-center justify-between shadow">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-bold uppercase tracking-wide">RUSH ORDER</div>
              <div className="text-xs text-red-100">
                {rushWaived ? 'Surcharge waived' : `Surcharge: $${rushSurchargeAmount.toFixed(2)}`}
                {' · '}Production queue priority active
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={toggleWaiver} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold">
              {rushWaived ? 'Restore Surcharge' : 'Waive Surcharge'}
            </button>
            <button onClick={toggleRush} className="px-3 py-1.5 bg-white text-red-700 hover:bg-red-100 rounded text-xs font-bold">
              Remove RUSH
            </button>
          </div>
        </div>
      )}
      {(() => {
        const o: any = order;
        const nb = o.neededByDate || o.needed_by_date;
        if (!nb) return null;
        const isPaid = o.depositPaid || o.deposit_paid || o.paidInFull || o.paid_in_full || (Number(o.paymentAmount || o.payment_amount) || 0) > 0;
        // needed_by_date is a calendar date. new Date('2026-09-20') parses as UTC
        // midnight, which is the previous evening in Central — the ticker showed
        // the day before and one day fewer. Build it as a local calendar date.
        const m = String(nb).match(/^(\d{4})-(\d{2})-(\d{2})/);
        const due = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(nb);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const dueMid = new Date(due); dueMid.setHours(0, 0, 0, 0);
        const daysLeft = Math.round((dueMid.getTime() - today.getTime()) / 86400000);
        const dueStr = format(due, 'MMM d, yyyy');
        let cls = 'bg-green-600', label = '';
        if (!isPaid) {
          cls = 'bg-gray-500';
          label = `Deadline clock starts once payment is received — due ${dueStr}`;
        } else if (daysLeft < 0) {
          cls = 'bg-red-600';
          label = `OVERDUE by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} · was due ${dueStr}`;
        } else {
          cls = daysLeft <= 5 ? 'bg-amber-500' : 'bg-green-600';
          label = `${daysLeft} day${daysLeft !== 1 ? 's' : ''} to deadline · due ${dueStr}`;
        }
        return (
          <div className={`mb-4 px-4 py-2.5 ${cls} text-white rounded-lg flex items-center gap-3 shadow`}>
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-sm">{label}</span>
          </div>
        );
      })()}
      <PageHeader
        title={`Order ${order.orderNumber}`}
        subtitle={order.createdAt ? `Placed on ${format(new Date(order.createdAt), 'MMMM d, yyyy')}` : undefined}
        actions={
          <div className="flex flex-wrap gap-2">
            {!isRush && (
              <button
                onClick={toggleRush}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                title="Mark this order as RUSH — adds +25% surcharge and prioritizes in production queue"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Mark RUSH
              </button>
            )}
            {canEdit && (
              <button
                onClick={isPartsOrder ? handleOpenPartsEdit : handleOpenEditModal}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {isPartsOrder ? 'Edit Parts' : 'Edit Order'}
              </button>
            )}
            <button
              onClick={handleOpenLabelModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Labels
            </button>
            <button
              onClick={handlePrintCutSheet}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Print Cut Sheet
            </button>
            <button
              onClick={handlePrintProductionSheet}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Print Production Sheet
            </button>
            <button
              onClick={handlePrintOrder}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Print Order
            </button>
            <Link
              to="/orders"
              className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
            >
              Back to Orders
            </Link>
          </div>
        }
      />

      {/* Invoice Correction Requested Banner */}
      {(order.invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at) && (
        <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Dealer Requested Invoice Correction</h3>
              <p className="text-sm text-red-700 mt-1">
                {order.invoiceCorrectionMessage || (order as any).invoice_correction_message}
              </p>
              <p className="text-xs text-red-500 mt-1">
                Requested: {format(new Date(order.invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.clearInvoiceCorrection(order.id);
                  loadOrder();
                } catch (e) {
                  console.error('Failed to dismiss correction:', e);
                }
              }}
              className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 flex-shrink-0"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}


      {/* Admin Edit Audit */}
      {(order.adminEditedAt || order.admin_edited_at) && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-700">
            Last admin edit: {format(new Date(order.adminEditedAt || order.admin_edited_at!), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Order Details
          </button>
          <button
            onClick={() => setActiveTab('cutsheet')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cutsheet'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cut Sheet
          </button>
          <button
            onClick={() => setActiveTab('bom')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bom'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            BOM
          </button>
          <button
            onClick={() => setActiveTab('shipping')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'shipping'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Shipping
          </button>
          <button
            onClick={() => setActiveTab('qc')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'qc'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            QC Photos
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'progress'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Progress
          </button>
          <button
            onClick={() => setActiveTab('invoice')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invoice'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Invoice
          </button>
          <button
            onClick={() => setActiveTab('labor')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'labor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Labor
          </button>
          {canViewCost && (
            <button
              onClick={() => setActiveTab('cost')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cost'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cost Analysis
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'cutsheet' && (
        <CutSheetTab cutSheet={cutSheet} loading={bomLoading} order={order} />
      )}

      {activeTab === 'shipping' && (
        <ShippingTab order={order} onChange={loadOrder} />
      )}

      {activeTab === 'progress' && (
        <WorkflowProgressTab orderId={order.id} orderStatus={order.status} />
      )}

      {activeTab === 'labor' && (
        <LaborBomTab orderId={order.id} />
      )}

      {activeTab === 'invoice' && (
        <InvoiceTab order={order} onRefresh={loadOrder} />
      )}

      {activeTab === 'bom' && (
        <BomTab bom={bom} loading={bomLoading} order={order} />
      )}

      {activeTab === 'qc' && (
        <QcPhotosTab records={qcRecords} loading={qcLoading} lightbox={qcLightbox} setLightbox={setQcLightbox} onRefresh={loadQcRecords} />
      )}

      {activeTab === 'cost' && canViewCost && (
        <CostAnalysisTab
          costData={costData}
          comparison={costComparison}
          loading={costLoading}
          orderId={id || ''}
          freightCost={Number((order as any)?.freightCost ?? (order as any)?.freight_cost ?? 0)}
          onRecalculate={loadCostData}
        />
      )}

      {activeTab === 'details' && (
        <>
      {/* Shortage Hold Warning */}
      {(order as any).shortageHold && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-red-500 text-xl mt-0.5">&#9888;</div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 text-sm">Parts Shortage — Red Bin Hold</h3>
              <p className="text-red-700 text-sm mt-1">
                This order is on hold due to missing parts.
                {(order as any).shortageHoldAt && (
                  <span className="text-red-500 ml-1">Since {format(new Date((order as any).shortageHoldAt), 'MMM d, yyyy h:mm a')}</span>
                )}
              </p>
              {(order as any).shortageDetails && (() => {
                try {
                  const details = typeof (order as any).shortageDetails === 'string'
                    ? JSON.parse((order as any).shortageDetails)
                    : (order as any).shortageDetails;
                  if (Array.isArray(details) && details.length > 0) {
                    return (
                      <div className="mt-2 space-y-1">
                        {details.map((item: any, i: number) => (
                          <div key={i} className="text-sm text-red-700 flex items-center gap-2">
                            <span className="font-mono text-xs bg-red-100 px-1.5 py-0.5 rounded">{item.partNumber}</span>
                            <span>{item.description}</span>
                            <span className="text-red-500">— short {item.shortageQuantity || item.shortage_quantity} of {item.quantityNeeded || item.quantity_needed}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                } catch { return null; }
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Commission Exempt Toggle */}
      <div className="mb-4 flex items-center gap-3 px-1">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={(order as any).commissionExempt || (order as any).commission_exempt || false}
            onChange={async (e) => {
              const exempt = e.target.checked;
              try {
                await fetch(`${(import.meta as any).env.VITE_API_URL || '/api'}/orders/${id}/commission-exempt`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}` },
                  body: JSON.stringify({ exempt }),
                });
                setOrder((prev: any) => ({ ...prev, commissionExempt: exempt, commission_exempt: exempt }));
              } catch {}
            }}
            className="rounded text-red-600 focus:ring-red-500"
          />
          <span className="text-gray-700">No Commission</span>
          <span className="text-xs text-gray-400">(demo, showroom, etc.)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm ml-4">
          <input
            type="checkbox"
            checked={(order as any).isShowroomOrder || (order as any).is_showroom_order || false}
            onChange={async (e) => {
              const isShowroom = e.target.checked;
              try {
                await fetch(`${(import.meta as any).env.VITE_API_URL || '/api'}/orders/${id}/showroom-order`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}` },
                  body: JSON.stringify({ isShowroom }),
                });
                setOrder((prev: any) => ({
                  ...prev,
                  isShowroomOrder: isShowroom,
                  is_showroom_order: isShowroom,
                  ...(isShowroom ? { commissionExempt: true, commission_exempt: true } : {}),
                }));
              } catch {}
            }}
            className="rounded text-purple-600 focus:ring-purple-500"
          />
          <span className="text-gray-700">Showroom/Display Unit</span>
        </label>
      </div>

      {/* Customer Details Section */}
      {(customerName || customerPhone || customerEmail || customerStreet) && (
        <Card title="Customer Details" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {customerName && (
              <div>
                <p className="text-sm text-gray-500">Customer Name</p>
                <p className="font-medium">{customerName}</p>
              </div>
            )}
            {customerPhone && (
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{customerPhone}</p>
              </div>
            )}
            {customerEmail && (
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{customerEmail}</p>
              </div>
            )}
            {customerStreet && (
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium">
                  {customerStreet}
                  {customerCity && `, ${customerCity}`}
                  {customerState && `, ${customerState}`}
                  {customerZip && ` ${customerZip}`}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Info Card */}
          <Card title="Order Information">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Order Number</p>
                <p className="font-medium">{order.orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Type</p>
                <p className="font-medium capitalize">{order.orderType || 'Screen'}</p>
              </div>
              {order.sidemark && (
                <div>
                  <p className="text-sm text-gray-500">PO / Sidemark / Job Name</p>
                  <p className="font-medium">{order.sidemark}</p>
                </div>
              )}
              {order.poNumber && (
                <div>
                  <p className="text-sm text-gray-500">Screen Location</p>
                  <p className="font-medium">{order.poNumber}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Order Date</p>
                <p className="font-medium">{format(new Date(order.createdAt), 'MMM d, yyyy')}</p>
              </div>
              {orderedBy && (
                <div>
                  <p className="text-sm text-gray-500">Ordered By</p>
                  <p className="font-medium">{orderedBy}</p>
                </div>
              )}
              {neededByDate && (
                <div>
                  <p className="text-sm text-gray-500">Date Needed By</p>
                  <p className="font-medium">{format(new Date(neededByDate), 'MMM d, yyyy')}</p>
                </div>
              )}
              {authorizedBy && authorizedBy !== orderedBy && (
                <div>
                  <p className="text-sm text-gray-500">Authorized By</p>
                  <p className="font-medium">{authorizedBy}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Support Commission — assigning an employee here gives them a flat
              10% of the order total and OVERRIDES the regular dealer-sales-rep
              commission. For when an internal team member had to hand-hold the
              dealer through the order. */}
          {(() => {
            const total = Number((order as any).totalAmount ?? (order as any).total_amount ?? 0) || 0;
            const computed = Math.round(total * 0.10 * 100) / 100;
            const repId = (order as any).supportRepId || null;
            return (
              <Card title="Support Commission — 10% (overrides sales rep)">
                <div className="flex items-center gap-3">
                  <select
                    value={repId || ''}
                    onChange={async (e) => {
                      const v = e.target.value || null;
                      try {
                        const updated = await api.orders.setSupportCommission(order.id, { supportRepId: v });
                        setOrder(updated as any);
                      } catch (err: any) { alert(err.message); }
                    }}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white min-w-[200px]"
                  >
                    <option value="">— None (sales rep gets standard commission) —</option>
                    {employees.map((emp: any) => (
                      <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                    ))}
                  </select>
                  {repId && (
                    <span className="text-sm text-gray-700">
                      Earns <strong className="font-mono">${computed.toFixed(2)}</strong> (10% of ${total.toFixed(2)})
                      <span className="ml-2 text-xs text-amber-700 font-medium">— sales rep gets $0</span>
                    </span>
                  )}
                </div>
              </Card>
            );
          })()}

          {/* Controls & Accessories */}
          {!isPartsOrder && (
            <Card title={
              <div className="flex items-center justify-between w-full">
                <span>Controls & Accessories</span>
                {!['cancelled', 'completed'].includes(order.status) && !editingAccessories && (
                  <button
                    onClick={() => {
                      const remoteAcc = ((order as any).accessories || []).find((a: any) => a.accessory_type === 'remote');
                      const wsAcc = ((order as any).accessories || []).find((a: any) => a.accessory_type === 'wall_switch');
                      setAccForm({
                        remoteType: remoteAcc?.description || '',
                        remoteQty: remoteAcc ? parseInt(remoteAcc.quantity) || 0 : 0,
                        wallSwitchType: wsAcc?.description || 'Wall Switch',
                        wallSwitchQty: wsAcc ? parseInt(wsAcc.quantity) || 0 : 0,
                      });
                      setEditingAccessories(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                )}
              </div>
            }>
              {editingAccessories ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Remote Type</label>
                      <select
                        value={accForm.remoteType}
                        onChange={(e) => setAccForm(f => ({ ...f, remoteType: e.target.value, remoteQty: e.target.value ? Math.max(f.remoteQty, 1) : 0 }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">None</option>
                        <optgroup label="Alpha Pioneer">
                          <option value="alpha_pioneer_1ch">Alpha Pioneer 1-Channel</option>
                          <option value="alpha_pioneer_4ch">Alpha Pioneer 4-Channel</option>
                          <option value="alpha_pioneer_8ch_lcd">Alpha Pioneer 8-Ch LCD</option>
                          <option value="alpha_pioneer_16ch_lcd">Alpha Pioneer 16-Ch LCD</option>
                        </optgroup>
                        <optgroup label="Somfy">
                          <option value="somfy_situo_1ch">Somfy Situo 1-Channel</option>
                          <option value="somfy_situo_5ch">Somfy Situo 5-Channel</option>
                          <option value="somfy_telis_1ch">Somfy Telis 1-Channel</option>
                          <option value="somfy_telis_4ch">Somfy Telis 4-Channel</option>
                          <option value="somfy_telis_16ch">Somfy Telis 16-Channel</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Remote Qty</label>
                      <input
                        type="number"
                        min={0}
                        value={accForm.remoteQty}
                        onChange={(e) => setAccForm(f => ({ ...f, remoteQty: parseInt(e.target.value) || 0 }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Wall Switch Type</label>
                      <select
                        value={accForm.wallSwitchType}
                        onChange={(e) => setAccForm(f => ({ ...f, wallSwitchType: e.target.value, wallSwitchQty: e.target.value ? Math.max(f.wallSwitchQty, 1) : 0 }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">None</option>
                        <option value="alpha_rts_wall_switch">Alpha RTS Wall Switch</option>
                        <option value="somfy_rts_wall_switch">Somfy RTS Wall Switch</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Wall Switch Qty</label>
                      <input
                        type="number"
                        min={0}
                        value={accForm.wallSwitchQty}
                        onChange={(e) => setAccForm(f => ({ ...f, wallSwitchQty: parseInt(e.target.value) || 0 }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          setAccSaving(true);
                          await api.orders.adminEditOrder(order.id, {
                            remoteType: accForm.remoteType || undefined,
                            remoteQty: accForm.remoteType ? accForm.remoteQty : 0,
                            wallSwitchType: accForm.wallSwitchType || undefined,
                            wallSwitchQty: accForm.wallSwitchType ? accForm.wallSwitchQty : 0,
                          });
                          setEditingAccessories(false);
                          await loadOrder();
                        } catch (err: any) {
                          alert(err.message || 'Failed to save accessories');
                        } finally {
                          setAccSaving(false);
                        }
                      }}
                      disabled={accSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {accSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingAccessories(false)}
                      className="px-4 py-2 text-gray-600 text-sm hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Motor Type</p>
                      <p className="font-medium capitalize flex items-center gap-2">
                        {motorType}
                        {(motorType || '').toLowerCase() === 'alpha' && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-300" title="Alpha motor required — order Alpha motor stock for this order">
                            ALPHA
                          </span>
                        )}
                      </p>
                    </div>
                    {(() => {
                      const remoteAcc = ((order as any).accessories || []).find((a: any) => a.accessory_type === 'remote');
                      const rType = remoteAcc?.description || remoteType;
                      const rQty = remoteAcc?.quantity || remoteQty;
                      const rPrice = remoteAcc ? parseFloat(remoteAcc.total_price) || 0 : 0;
                      return (
                        <div>
                          <p className="text-sm text-gray-500">Remote</p>
                          <p className="font-medium">{formatRemoteDescription(rType)}{rQty > 1 ? ` x${rQty}` : ''}</p>
                          {rPrice > 0 && <p className="text-xs text-gray-400">${rPrice.toFixed(2)}</p>}
                        </div>
                      );
                    })()}
                    {wallSwitchQty > 0 && (
                      <div>
                        <p className="text-sm text-gray-500">Wall Switch</p>
                        <p className="font-medium">Qty: {wallSwitchQty}</p>
                      </div>
                    )}
                  </div>
                  {/* Non-remote accessories (fabric panels, upgrades, etc.) */}
                  {((order as any).accessories || []).filter((a: any) => a.accessory_type !== 'remote' && a.accessory_type !== 'uchannel').length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500 mb-2">Added Accessories</p>
                      <div className="space-y-2">
                        {((order as any).accessories || [])
                          .filter((a: any) => a.accessory_type !== 'remote' && a.accessory_type !== 'uchannel')
                          .map((acc: any, i: number) => (
                            <div key={acc.id || i} className="flex justify-between items-center text-sm">
                              <div>
                                <span className="font-medium">{acc.description}</span>
                                <span className="text-gray-400 ml-2">x{acc.quantity}</span>
                              </div>
                              <span className="font-medium">${(parseFloat(acc.total_price) || 0).toFixed(2)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* Comments Section */}
          {comments && (
            <Card title="Comments / Special Instructions">
              <p className="text-gray-700 whitespace-pre-wrap">{comments}</p>
            </Card>
          )}

          {/* Visualizer Preview Section */}
          {((order as any).visualizerImageUrl || (order as any).visualizerConfig) && (
            <Card title="Visualizer Preview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(order as any).visualizerImageUrl && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Customer Preview</p>
                    <img
                      src={(order as any).visualizerImageUrl}
                      alt="Patio visualizer preview"
                      className="w-full max-w-sm rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                {(order as any).visualizerConfig && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Configuration</p>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        {(order as any).visualizerConfig.fabricType && (
                          <>
                            <span className="text-gray-500">Fabric:</span>
                            <span className="text-gray-900">{(order as any).visualizerConfig.fabricType}</span>
                          </>
                        )}
                        {(order as any).visualizerConfig.fabricColor && (
                          <>
                            <span className="text-gray-500">Color:</span>
                            <span className="text-gray-900">{(order as any).visualizerConfig.fabricColor}</span>
                          </>
                        )}
                        {(order as any).visualizerConfig.systemColor && (
                          <>
                            <span className="text-gray-500">System Color:</span>
                            <span className="text-gray-900">{(order as any).visualizerConfig.systemColor}</span>
                          </>
                        )}
                        {(order as any).visualizerConfig.dimensions && (
                          <>
                            <span className="text-gray-500">Dimensions:</span>
                            <span className="text-gray-900">
                              {(order as any).visualizerConfig.dimensions.width}" x {(order as any).visualizerConfig.dimensions.height}"
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Linked Screen Designs */}
          {linkedDesigns.length > 0 && (
            <Card title={`Screen Designs (${linkedDesigns.length} opening${linkedDesigns.length !== 1 ? 's' : ''})`}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {linkedDesigns.map((design: any) => (
                  <div
                    key={design.id}
                    className="cursor-pointer group"
                    onClick={() => setExpandedDesign(expandedDesign === design.id ? null : design.id)}
                  >
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 group-hover:border-blue-400 transition-colors">
                      <img
                        src={design.image_url}
                        alt={design.name}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <span className="text-white text-xs font-medium">
                          {design.screen_count || 0} screen{(design.screen_count || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{design.name}</p>
                  </div>
                ))}
              </div>
              {expandedDesign && linkedDesigns.find((d: any) => d.id === expandedDesign) && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-md font-medium">
                      {linkedDesigns.find((d: any) => d.id === expandedDesign)!.name}
                    </h3>
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedDesign(null); }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <img
                    src={linkedDesigns.find((d: any) => d.id === expandedDesign)!.image_url}
                    alt="Design"
                    className="w-full max-w-2xl rounded-lg border border-gray-200"
                  />
                  {linkedDesigns.find((d: any) => d.id === expandedDesign)!.notes && (
                    <p className="text-sm text-gray-600 mt-2">
                      {linkedDesigns.find((d: any) => d.id === expandedDesign)!.notes}
                    </p>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Order Items */}
          <Card title={isPartsOrder ? `Parts (${groupPartsItems(order.items || []).length})` : `Screen Units (${order.items?.length || 0})`}>
            <div className="space-y-4">
              {order.items && order.items.length > 0 ? (
                isPartsOrder ? (
                  groupPartsItems(order.items).map((grp: any, index: number) => {
                    const partsInfo = grp.info;
                    return (
                      <div key={grp.id || index} className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">
                              {partsInfo?.name || 'Part'}
                              {grp.quantity > 1 && <span className="text-gray-500 font-normal"> × {grp.quantity}</span>}
                            </p>
                            <div className="text-sm text-gray-600 space-y-0.5 mt-1">
                              {partsInfo?.fabricType && <p>Fabric: {partsInfo.fabricType}</p>}
                              {partsInfo?.fabricColor && <p>Color: {partsInfo.fabricColor}</p>}
                              {partsInfo?.fabricOrderType && <p>Type: {partsInfo.fabricOrderType === 'panel_replacement' ? 'Panel Replacement' : 'Fabric Only'}</p>}
                              {partsInfo?.width && partsInfo?.height && <p>Size: {partsInfo.width}" W × {partsInfo.height}" H</p>}
                              {/* For cut parts (u-channel etc.) the raw description carries
                                  the order-time note "(Factory +6")", which misreads on the
                                  floor as "add 6 → cut 150". The +6 is ALREADY baked into
                                  lengthInches, so the clean "Cut 144"" label below is the
                                  cut. Suppress the raw description whenever we have that. */}
                              {partsInfo?.description && !partsInfo?.width && !grp.lengthLabel && <p>{partsInfo.description}</p>}
                              {!partsInfo?.fabricType && partsInfo?.color && <p>Color: {partsInfo.color}</p>}
                              {partsInfo?.sku && <p>SKU: {partsInfo.sku}</p>}
                              {grp.lengthLabel && <p>{grp.lengthLabel}</p>}
                              {/* Cut parts price per piece, so a mixed-length line has
                                  no single unit price. Say so rather than imply one. */}
                              {grp.mixedPricing && <p className="text-gray-500 italic">priced per length</p>}
                            </div>
                          </div>
                          <span className="font-semibold text-gray-900">
                            ${Number(grp.lineTotal || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  order.items.map((item, index) => (
                    <OrderItemCard key={item.id} item={item} index={index} orderInfo={orderInfo} orderId={order.id} onEditItem={!['cancelled', 'completed'].includes(order.status) ? handleOpenItemEdit : undefined} />
                  ))
                )
              ) : (
                <p className="text-gray-500 italic">No items in this order</p>
              )}
            </div>
          </Card>

          {/* Customer Notes (from dealer) */}
          {order.notes && (
            <Card title="Customer Notes">
              <p className="text-gray-700 whitespace-pre-wrap">{order.notes}</p>
            </Card>
          )}

          {/* Internal Notes (factory only) */}
          <Card
            title="Internal Notes"
            action={
              <button
                onClick={() => setShowNoteModal(true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Note
              </button>
            }
          >
            {order.internalNotes ? (
              <div className="space-y-2">
                {order.internalNotes.split('\n').map((line: string, i: number) => (
                  <div key={i} className="flex gap-2 text-sm">
                    {line.match(/^\[\d{4}-\d{2}-\d{2}\]/) ? (
                      <>
                        <span className="text-gray-400 font-mono shrink-0">{line.match(/^\[([^\]]+)\]/)?.[1]}</span>
                        <span className="text-gray-700">{line.replace(/^\[[^\]]+\]\s*/, '')}</span>
                      </>
                    ) : (
                      <span className="text-gray-700">{line}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 italic text-sm">No internal notes yet. These notes are only visible to factory staff.</p>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card title="Status">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Current Status</span>
                <div className="flex items-center gap-1.5">
                  {order.status === 'ready_to_ship' && ['will_call', 'willcall'].includes(String((order as any).shippingMethod || (order as any).shipping_method || '').toLowerCase()) ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" title="Will Call — ready for the dealer to pick up">
                      Ready for Pickup
                    </span>
                  ) : (
                    <StatusBadge status={order.status} type="order" />
                  )}
                  {(order as any).shortageHold && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-600 text-white animate-pulse">RED BIN</span>
                  )}
                </div>
              </div>

              {/* Payment Status */}
              <div className="flex items-center justify-between pt-2 border-t mt-2">
                <span className="text-gray-600">Payment</span>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                {isPaidInFull ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    Paid in Full
                  </span>
                ) : isDepositPaid ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    Deposit Paid
                  </span>
                ) : isCreditApproved ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {order.creditTransactionId ? 'Net Credit' : 'Credit Approved'}
                  </span>
                ) : isSubmittedForInvoicing ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                    Submitted for Invoicing
                  </span>
                ) : order.status === 'pending_payment' && hasStripeSession ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                    Awaiting Stripe Payment
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                    Pending
                  </span>
                )}
                {pendingAch > 0.5 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800" title="A bank/ACH payment has been initiated and is clearing (2–5 business days)">
                    ACH Pending ${pendingAch.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
                </div>
              </div>
              {/* Payment Details */}
              {(isDepositPaid || isPaidInFull) && (
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                  {Number((order as any).paymentAmount || (order as any).payment_amount || 0) > 0 && (
                    <p>${Number((order as any).paymentAmount || (order as any).payment_amount || 0).toFixed(2)} received</p>
                  )}
                  {((order as any).paymentMethod || (order as any).payment_method) && (
                    <p>
                      via {({
                        stripe_card: 'Credit Card (Stripe)',
                        stripe_ach: 'ACH (Stripe)',
                        authorize_net: 'Card (Authorize.net)',
                        check: 'Check',
                        wire_transfer: 'Wire Transfer',
                        cash: 'Cash',
                        other: 'Other',
                      } as any)[(order as any).paymentMethod || (order as any).payment_method] || (order as any).paymentMethod || (order as any).payment_method}
                      {((order as any).paymentReference || (order as any).payment_reference) && (
                        <> #{(order as any).paymentReference || (order as any).payment_reference}</>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Assigned Bin */}
              <div className="flex items-center justify-between pt-2 border-t mt-2">
                <span className="text-gray-600">Production Bin</span>
                {assignedBin ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                    {assignedBin.bin_number}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">Not assigned</span>
                )}
              </div>

              {/* Admin Override: Approve Without Deposit */}
              {!isDepositPaid && !isCreditApproved && (
                <div className="pt-3 mt-3 border-t border-dashed">
                  <button
                    onClick={handleApproveWithoutDeposit}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {actionLoading ? 'Processing...' : 'Approve Without Deposit'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Admin override for trusted dealers
                  </p>
                </div>
              )}

              {/* Record Payment Button */}
              {!isPaidInFull && order.status !== 'cancelled' && (
                <div className="pt-3 mt-3 border-t border-dashed">
                  <button
                    onClick={() => {
                      const totalAmt = Number((order as any).totalAmount || (order as any).total_amount || 0);
                      const paidAmt = Number((order as any).paymentAmount || (order as any).payment_amount || 0);
                      const remaining = Math.round((totalAmt - paidAmt) * 100) / 100;
                      const depositAmt = Math.round(totalAmt * 0.5 * 100) / 100;
                      setPaymentForm({
                        amount: '',
                        payment_type: 'partial',
                        payment_method: 'check',
                        payment_reference: '',
                        notes: '',
                      });
                      setPaymentError(null);
                      setShowPaymentModal(true);
                    }}
                    className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Record Payment
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Check, wire transfer, cash, etc.
                  </p>
                </div>
              )}

              {/* Issue Refund / Credit */}
              {Number((order as any).paymentAmount || 0) > 0 && order.status !== 'cancelled' && (() => {
                const totalAmt = Number((order as any).totalAmount || 0);
                const paidAmt = Number((order as any).paymentAmount || 0);
                const achDisc = Number((order as any).achDiscountAmount || 0);
                const refunded = Number((order as any).refundedAmount || 0);
                // Effective dealer credit = cash paid - refunded + ACH discount earned.
                // Overpayment surfaces when that exceeds the order total.
                const overpayment = Math.round((paidAmt - refunded + achDisc - totalAmt) * 100) / 100;
                return (
                  <div className="pt-3 mt-3 border-t border-dashed">
                    {overpayment > 0.01 && (
                      <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                        Overpayment: <strong className="font-mono">${overpayment.toFixed(2)}</strong>
                      </div>
                    )}
                    {refunded > 0 && (
                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                        Refunded: <strong className="font-mono">${refunded.toFixed(2)}</strong>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setRefundForm({
                          amount: overpayment > 0 ? overpayment.toFixed(2) : '',
                          method: 'check',
                          reference: '',
                          reason: '',
                        });
                        setRefundError(null);
                        setShowRefundModal(true);
                      }}
                      className="w-full px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Issue Refund / Credit
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Cut a check, send ACH back, or credit dealer's account
                    </p>
                  </div>
                );
              })()}

              {/* Status Actions */}
              {availableTransitions.length > 0 && (
                <div className="space-y-2 pt-3 mt-3 border-t">
                  {(order.status === 'pending_release' || order.status === 'approved') && (
                    <>
                      <button
                        onClick={handleReleaseOrder}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 bg-green-600 hover:bg-green-700"
                      >
                        {actionLoading ? 'Processing...' : 'Push to Production'}
                      </button>
                      {releaseError && (
                        <p className="text-xs text-red-600 mt-1">{releaseError}</p>
                      )}
                    </>
                  )}
                  {availableTransitions.filter(s => s !== 'cancelled' && (s !== 'in_production' || !['submitted', 'pending_release', 'approved'].includes(order.status))).map(status => {
                    // Will-call orders are PICKED UP, not shipped. Relabel the
                    // ready_to_ship → shipped action and target a terminal state:
                    // screens → 'delivered' (dealer still installs/registers warranty),
                    // parts/special → 'completed'.
                    const isWillCall = ['will_call', 'willcall'].includes(
                      String((order as any).shippingMethod || (order as any).shipping_method || '').toLowerCase());
                    if (status === 'shipped' && isWillCall) {
                      const isParts = String((order as any).orderType || (order as any).order_type || '').toLowerCase() === 'parts';
                      const pickupStatus = isParts ? 'completed' : 'delivered';
                      return (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(pickupStatus, 'Picked up (will call)')}
                          disabled={actionLoading}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {actionLoading ? 'Processing...' : 'Mark as Picked Up'}
                        </button>
                      );
                    }
                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {actionLoading ? 'Processing...' : `Mark as ${formatStatus(status)}`}
                      </button>
                    );
                  })}
                  {availableTransitions.includes('cancelled') && (
                    <button
                      onClick={() => handleStatusChange('cancelled')}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 mt-2"
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              )}

              {/* Reject for Correction */}
              {['submitted', 'pending_release'].includes(order.status) && (
                <div className="pt-3 mt-3 border-t border-dashed">
                  <button
                    onClick={() => {
                      setCorrectionReason('');
                      setCorrectionNotes('');
                      setShowCorrectionModal(true);
                    }}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Reject for Correction
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Send back to dealer to fix
                  </p>
                </div>
              )}

              {/* Correction Info Banner */}
              {order.status === 'needs_correction' && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-orange-800 font-semibold text-sm">Sent Back for Correction</p>
                  {(order as any).correctionReason && (
                    <p className="text-orange-700 text-sm mt-1">{(order as any).correctionReason}</p>
                  )}
                  {(order as any).correctionRequestedAt && (
                    <p className="text-orange-400 text-xs mt-1">{new Date((order as any).correctionRequestedAt).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Dealer Info */}
          <Card title="Dealer">
            <div className="space-y-2">
              <p className="font-medium text-gray-900">{order.companyName || 'Unknown Dealer'}</p>
              {order.contactName && <p className="text-sm text-gray-600">{order.contactName}</p>}
              {order.contactEmail && <p className="text-sm text-gray-600">{order.contactEmail}</p>}
              {order.contactPhone && <p className="text-sm text-gray-600">{order.contactPhone}</p>}
              {order.dealerId && (
                <Link
                  to={`/dealers/${order.dealerId}`}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View Dealer Profile &rarr;
                </Link>
              )}
            </div>
          </Card>

          {/* Sales Attribution — who gets credit for this order */}
          {(order as any).salesAttribution && (() => {
            const a = (order as any).salesAttribution;
            const hasAny = a.repFirm || a.kederSalesRep || a.dealerRep || a.supportRep;
            if (!hasAny) return null;
            const accountTypeLabel = a.accountType === 'rep'
              ? <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Rep Firm Account</span>
              : a.accountType === 'direct'
                ? <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Direct Account</span>
                : a.accountType === 'house'
                  ? <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">House Account (no commission)</span>
                  : null;
            return (
              <Card title="Sales Attribution">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-500">Account Type:</span>
                    {accountTypeLabel || <span className="ml-2 text-gray-700">{a.accountType || '—'}</span>}
                  </div>
                  {a.repFirm && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-purple-600 font-semibold">Rep Firm</p>
                      <p className="font-medium text-gray-900">{a.repFirm.name}</p>
                    </div>
                  )}
                  {a.kederSalesRep && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">Keder Sales Rep</p>
                      <p className="font-medium text-gray-900">{a.kederSalesRep.name}</p>
                      {a.kederSalesRep.email && <p className="text-xs text-gray-500">{a.kederSalesRep.email}</p>}
                    </div>
                  )}
                  {a.dealerRep && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Dealer's Sales Rep</p>
                      <p className="font-medium text-gray-900">{a.dealerRep.name}{a.dealerRep.role ? <span className="text-xs text-gray-500 ml-2">({a.dealerRep.role.replace('_', ' ')})</span> : null}</p>
                      {a.dealerRep.email && <p className="text-xs text-gray-500">{a.dealerRep.email}</p>}
                    </div>
                  )}
                  {a.supportRep && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-green-600 font-semibold">Support Rep (this order)</p>
                      <p className="font-medium text-gray-900">{a.supportRep.name}</p>
                      {a.supportRep.email && <p className="text-xs text-gray-500">{a.supportRep.email}</p>}
                    </div>
                  )}
                </div>
              </Card>
            );
          })()}

          {/* Shipping */}
          <Card title="Shipping">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Method</p>
                <p className="font-medium">{formatShippingMethod(order.shippingMethod)}</p>
              </div>
              {order.shipToAddress && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Address</p>
                    {!editingShipAddress ? (
                      <button
                        onClick={() => {
                          const addr = typeof order.shipToAddress === 'string' ? JSON.parse(order.shipToAddress) : order.shipToAddress;
                          setShipAddressForm({
                            street: addr?.street || '',
                            city: addr?.city || '',
                            state: addr?.state || '',
                            zip: addr?.zip || '',
                          });
                          setEditingShipAddress(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          onClick={async () => {
                            try {
                              await api.orders.updateOrder(order.id, { shipToAddress: shipAddressForm });
                              setOrder({ ...order, shipToAddress: shipAddressForm });
                              setEditingShipAddress(false);
                            } catch (err: any) {
                              setError(err.message || 'Failed to update address');
                            }
                          }}
                          className="text-xs text-green-600 hover:text-green-800 font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingShipAddress(false)}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  {editingShipAddress ? (
                    <div className="space-y-2 mt-1">
                      <input
                        type="text"
                        value={shipAddressForm.street}
                        onChange={e => setShipAddressForm({ ...shipAddressForm, street: e.target.value })}
                        placeholder="Street"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={shipAddressForm.city}
                          onChange={e => setShipAddressForm({ ...shipAddressForm, city: e.target.value })}
                          placeholder="City"
                          className="col-span-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <input
                          type="text"
                          value={shipAddressForm.state}
                          onChange={e => setShipAddressForm({ ...shipAddressForm, state: e.target.value })}
                          placeholder="State"
                          className="col-span-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <input
                          type="text"
                          value={shipAddressForm.zip}
                          onChange={e => setShipAddressForm({ ...shipAddressForm, zip: e.target.value })}
                          placeholder="ZIP"
                          className="col-span-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-900 whitespace-pre-line">
                      {formatAddress(order.shipToAddress)}
                    </p>
                  )}
                </div>
              )}

              {/* Freight Options as Badges */}
              {(liftgateDelivery || residentialDelivery || insideDelivery) && (
                <div className="pt-3 border-t">
                  <p className="text-sm text-gray-500 mb-2">Freight Options</p>
                  <div className="flex flex-wrap gap-2">
                    {liftgateDelivery && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Liftgate Required
                      </span>
                    )}
                    {residentialDelivery && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Residential Delivery
                      </span>
                    )}
                    {insideDelivery && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        Inside Delivery
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(order.shippingCarrier || order.selectedCarrierName || order.selected_carrier_name) && (
                <div>
                  <p className="text-sm text-gray-500">Carrier</p>
                  <p className="font-medium">{order.shippingCarrier || order.selectedCarrierName || order.selected_carrier_name}</p>
                </div>
              )}
              {order.trackingNumber && (
                <div>
                  <p className="text-sm text-gray-500">Tracking</p>
                  <p className="font-medium text-blue-600">{order.trackingNumber}</p>
                </div>
              )}
              {order.estimatedShipDate && (
                <div>
                  <p className="text-sm text-gray-500">Est. Ship Date</p>
                  <p className="font-medium">{format(new Date(order.estimatedShipDate), 'MMM d, yyyy')}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Order Summary */}
          <Card title="Summary">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Items</span>
                <span className="font-medium">{order.items?.length || 0} {isPartsOrder ? 'items' : 'screens'}</span>
              </div>
              {/* Per-unit line items */}
              {order.items?.map((item: any, idx: number) => {
                // Parts orders store details in notes JSON
                let partsInfo: any = null;
                if (isPartsOrder && item.notes) {
                  try {
                    partsInfo = typeof item.notes === 'string' ? JSON.parse(item.notes) : item.notes;
                  } catch { /* ignore parse errors */ }
                }

                return (
                  <div key={item.id || idx} className="text-sm space-y-1 pl-2 border-l-2 border-gray-200">
                    <div className="flex justify-between">
                      {isPartsOrder && partsInfo ? (
                        <span className="text-gray-500">
                          {partsInfo.name || item.productType || 'Part'}
                          {partsInfo.fabricType && !partsInfo.name?.includes(partsInfo.fabricType) ? ` — ${partsInfo.fabricType}` : ''}
                          {partsInfo.fabricColor && !partsInfo.name?.includes(partsInfo.fabricColor) ? ` ${partsInfo.fabricColor}` : ''}
                          {partsInfo.width && partsInfo.height ? ` — ${partsInfo.width}" × ${partsInfo.height}"` : ''}
                          {!partsInfo.fabricType && partsInfo.color ? ` — ${partsInfo.color}` : ''}
                          {partsInfo.sku ? ` (${partsInfo.sku})` : ''}
                          {(partsInfo.quantity || 0) > 1 ? ` x${partsInfo.quantity}` : ''}
                          {partsInfo.lengthInches ? ` — ${partsInfo.lengthInches}"` : ''}
                        </span>
                      ) : (
                        <span className="text-gray-500">Unit #{item.itemNumber || idx + 1} — Screen</span>
                      )}
                      <span>${(item.unitPrice || item.totalPrice || 0).toFixed(2)}</span>
                    </div>
                    {!isPartsOrder && (item.uChannelPrice || 0) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span className="pl-2">U-Channel ({item.uChannel === 'two_sides' || item.uChannel === 'both_sides' ? '2 sides' : '1 side'})</span>
                        <span>+${(item.uChannelPrice).toFixed(2)}</span>
                      </div>
                    )}
                    {!isPartsOrder && (item.extrasPrice || 0) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span className="pl-2">Extras</span>
                        <span>+${(item.extrasPrice).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Accessories (remotes, fabric panels, upgrades, etc.) */}
              {((order as any).accessories || []).length > 0 && (
                <div className="text-sm space-y-1 pl-2 border-l-2 border-blue-200">
                  {((order as any).accessories || []).map((acc: any, i: number) => (
                    <div key={acc.id || i} className="flex justify-between">
                      <span className="text-gray-500">
                        {acc.description || acc.accessory_type}{acc.quantity > 1 ? ` x${acc.quantity}` : ''}
                      </span>
                      <span>+${(parseFloat(acc.total_price) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between pt-1 border-t">
                <span className="text-gray-600">Subtotal</span>
                <span>${(order.subtotal || 0).toFixed(2)}</span>
              </div>
              {(order.discountAmount || 0) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({order.discountRate || 0}%)</span>
                  <span>-${order.discountAmount.toFixed(2)}</span>
                </div>
              )}
              {(order.taxAmount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span>${order.taxAmount.toFixed(2)}</span>
                </div>
              )}
              {(order.packagingAmount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Packaging</span>
                  <span>${order.packagingAmount.toFixed(2)}</span>
                </div>
              )}
              {(order.shippingAmount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span>${order.shippingAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t font-semibold">
                <span>Total</span>
                <span>${(order.totalAmount || 0).toFixed(2)}</span>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Timeline">
            <div className="space-y-3">
              {order.createdAt && (
                <TimelineItem label="Created" date={order.createdAt} />
              )}
              {order.submittedAt && (
                <TimelineItem label="Submitted" date={order.submittedAt} />
              )}
              {/* Payment: show "Paid in Full" if deposit + balance landed in one
                  transaction (same timestamp); otherwise show both steps. */}
              {order.depositPaidAt && order.paidInFullAt && new Date(order.depositPaidAt).getTime() === new Date(order.paidInFullAt).getTime() ? (
                <TimelineItem label="Paid in Full" date={order.paidInFullAt} />
              ) : (
                <>
                  {order.depositPaidAt && (
                    <TimelineItem label="Deposit Paid" date={order.depositPaidAt} />
                  )}
                  {order.paidInFullAt && (
                    <TimelineItem label="Paid in Full" date={order.paidInFullAt} />
                  )}
                </>
              )}
              {order.approvedAt && (
                <TimelineItem label="Approved" date={order.approvedAt} />
              )}
              {order.releasedAt && (
                <TimelineItem label="Released to Production" date={order.releasedAt} />
              )}
              {order.actualShipDate && (
                <TimelineItem label="Shipped" date={order.actualShipDate} />
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Order Activity Log */}
      {(order as any).history && (order as any).history.length > 0 && (
        <Card className="mt-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Activity Log
          </h3>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {(order as any).history.map((entry: any, i: number) => {
              const actionColors: Record<string, string> = {
                created: 'bg-blue-100 text-blue-700',
                submitted: 'bg-indigo-100 text-indigo-700',
                approved: 'bg-green-100 text-green-700',
                released: 'bg-purple-100 text-purple-700',
                status_change: 'bg-yellow-100 text-yellow-700',
                payment_received: 'bg-emerald-100 text-emerald-700',
                invoice_sent: 'bg-cyan-100 text-cyan-700',
                shipped: 'bg-teal-100 text-teal-700',
                delivered: 'bg-green-100 text-green-800',
                cancelled: 'bg-red-100 text-red-700',
                correction_requested: 'bg-orange-100 text-orange-700',
                updated: 'bg-gray-100 text-gray-700',
              };
              const colorClass = actionColors[entry.action] || 'bg-gray-100 text-gray-600';
              return (
                <div key={entry.id || i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${colorClass}`}>
                    {(entry.action || '').replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700">{entry.notes || '-'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {(() => {
                        const name = (entry as any).changedByName || (entry as any).changed_by_name;
                        const email = (entry as any).changedByEmail || (entry as any).changed_by_email;
                        if (name) {
                          return <span title={email || ''}>{name} · </span>;
                        }
                        // Fallback when name lookup didn't resolve — show "system"
                        // for null changed_by, and a short UUID prefix otherwise.
                        if (entry.changed_by) {
                          return <span title={entry.changed_by}>{String(entry.changed_by).slice(0, 8)}… · </span>;
                        }
                        return <span>system · </span>;
                      })()}
                      {new Date(entry.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      </>
      )}

      {/* Cancel Order Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Order</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to cancel order <span className="font-semibold">{order?.orderNumber}</span>?
            </p>
            {order && ['in_production', 'ready_to_ship', 'shipped', 'delivered'].includes(order.status) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700 font-medium">
                  This order is currently <span className="font-bold">{formatStatus(order.status)}</span>.
                  Cancelling will not undo production work already completed.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Yes, Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject for Correction Modal */}
      {showCorrectionModal && order && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reject for Correction</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Send order <span className="font-semibold">{order.orderNumber}</span> back to the dealer for correction. They'll receive an email with the reason.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <select
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select a reason...</option>
                  <option value="Missing accessories/remotes">Missing accessories/remotes</option>
                  <option value="Incorrect measurements">Incorrect measurements</option>
                  <option value="Wrong fabric/color selection">Wrong fabric/color selection</option>
                  <option value="Shipping information incomplete">Shipping information incomplete</option>
                  <option value="Customer information missing">Customer information missing</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional notes (optional)</label>
                <textarea
                  value={correctionNotes}
                  onChange={(e) => setCorrectionNotes(e.target.value)}
                  rows={3}
                  placeholder="Any additional details for the dealer..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowCorrectionModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const reason = correctionNotes
                    ? `${correctionReason}: ${correctionNotes}`
                    : correctionReason;
                  if (!reason) return;
                  try {
                    setActionLoading(true);
                    await api.orders.rejectForCorrection(order.id, reason);
                    setShowCorrectionModal(false);
                    await loadOrder();
                  } catch (err: any) {
                    console.error('Failed to reject for correction:', err);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading || !correctionReason}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {actionLoading ? 'Sending...' : 'Send Back to Dealer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal Note Modal */}
      {showScheduleModal && order && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Schedule Production</h3>
            <p className="text-xs text-gray-500 mb-4">
              Sets the order's production date on the calendar. Dealer requested by:{' '}
              <strong>{(order as any).neededByDate || (order as any).needed_by_date
                ? format(new Date((order as any).neededByDate || (order as any).needed_by_date), 'MMM d, yyyy')
                : '—'}</strong>
            </p>
            <label className="block text-sm text-gray-600 mb-1">Production date</label>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
            />
            <label className="block text-sm text-gray-600 mb-1">Priority</label>
            <select
              value={schedulePriority}
              onChange={(e) => setSchedulePriority(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-5"
            >
              <option value="rush">Rush</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={closeScheduleModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveSchedule} disabled={actionLoading}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {actionLoading ? 'Saving…' : 'Save Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Add Internal Note</h3>
            <p className="text-xs text-gray-500 mb-4">Only visible to factory staff — dealers will not see this.</p>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Enter internal note..."
              autoFocus
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => { setShowNoteModal(false); setNoteText(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={actionLoading || !noteText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && order && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Record Payment</h3>
            {(() => {
              const totalAmt = Number((order as any).totalAmount || (order as any).total_amount || 0);
              const paidAmt = Number((order as any).paymentAmount || (order as any).payment_amount || 0);
              const remaining = Math.round((totalAmt - paidAmt) * 100) / 100;
              return (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Order Total:</span> <span className="font-medium">${totalAmt.toFixed(2)}</span></div>
                  {paidAmt > 0 && <div className="flex justify-between"><span className="text-gray-500">Already Paid:</span> <span className="font-medium text-green-600">${paidAmt.toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t pt-1"><span className="text-gray-500 font-medium">Balance Due:</span> <span className="font-bold">${remaining.toFixed(2)}</span></div>
                </div>
              );
            })()}

            {paymentError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{paymentError}</div>
            )}

            <div className="space-y-3">
              {/* Amount - primary input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value, payment_type: 'partial' }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                  placeholder="Enter payment amount"
                  autoFocus
                />
              </div>

              {/* Quick-fill buttons */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Quick Fill</label>
                <div className="flex gap-2">
                  {(() => {
                    const totalAmt = Number((order as any).totalAmount || (order as any).total_amount || 0);
                    const paidAmt = Number((order as any).paymentAmount || (order as any).payment_amount || 0);
                    const remaining = Math.round((totalAmt - paidAmt) * 100) / 100;
                    const depositAmt = Math.round(totalAmt * 0.5 * 100) / 100;
                    return (
                      <>
                        {paidAmt === 0 && (
                          <button
                            type="button"
                            onClick={() => setPaymentForm(f => ({ ...f, amount: String(depositAmt), payment_type: 'deposit' }))}
                            className={`px-3 py-1.5 text-sm rounded-lg border ${paymentForm.payment_type === 'deposit' && paymentForm.amount === String(depositAmt) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                          >
                            50% Deposit (${depositAmt.toFixed(2)})
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setPaymentForm(f => ({ ...f, amount: String(remaining), payment_type: 'full' }))}
                          className={`px-3 py-1.5 text-sm rounded-lg border ${paymentForm.payment_type === 'full' && paymentForm.amount === String(remaining) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {paidAmt > 0 ? 'Remaining' : 'Full'} Balance (${remaining.toFixed(2)})
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="check">Check</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference # (optional)</label>
                <input
                  type="text"
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm(f => ({ ...f, payment_reference: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Check #, wire reference, etc."
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional details..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={actionLoading || !paymentForm.amount}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {actionLoading ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund / Credit Modal */}
      {showRefundModal && order && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Issue Refund / Credit</h3>
            {(() => {
              const totalAmt = Number((order as any).totalAmount || 0);
              const paidAmt = Number((order as any).paymentAmount || 0);
              const achDisc = Number((order as any).achDiscountAmount || 0);
              const refundedAmt = Number((order as any).refundedAmount || 0);
              const overpayment = Math.round((paidAmt - refundedAmt + achDisc - totalAmt) * 100) / 100;
              return (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Order Total:</span> <span className="font-medium">${totalAmt.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Paid:</span> <span className="font-medium">${paidAmt.toFixed(2)}</span></div>
                  {achDisc > 0 && <div className="flex justify-between"><span className="text-gray-500">ACH discount earned:</span> <span className="font-medium text-emerald-700">+${achDisc.toFixed(2)}</span></div>}
                  {refundedAmt > 0 && <div className="flex justify-between"><span className="text-gray-500">Already refunded:</span> <span className="font-medium text-rose-600">−${refundedAmt.toFixed(2)}</span></div>}
                  {overpayment > 0.01 && <div className="flex justify-between border-t pt-1"><span className="text-gray-500 font-medium">Overpayment:</span> <span className="font-bold text-amber-700">${overpayment.toFixed(2)}</span></div>}
                </div>
              );
            })()}

            {refundError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{refundError}</div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 text-lg"
                  placeholder="Enter refund amount"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                <select
                  value={refundForm.method}
                  onChange={(e) => setRefundForm(f => ({ ...f, method: e.target.value as any }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                >
                  <option value="check">Check (cut & mailed)</option>
                  <option value="ach">ACH (sent back)</option>
                  <option value="cash">Cash</option>
                  <option value="wire">Wire transfer</option>
                  <option value="credit_to_account">Credit to dealer's account (no money out)</option>
                  <option value="other">Other</option>
                </select>
                {refundForm.method === 'credit_to_account' && (
                  <p className="text-xs text-blue-700 mt-1">
                    Funds stay with Keder. A credit balance is added to the dealer's account ledger to draw against future orders.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference {refundForm.method === 'check' && '(check number)'}</label>
                <input
                  type="text"
                  value={refundForm.reference}
                  onChange={(e) => setRefundForm(f => ({ ...f, reference: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                  placeholder={refundForm.method === 'check' ? 'e.g. 4287' : refundForm.method === 'ach' ? 'transaction ID' : 'optional reference'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm(f => ({ ...f, reason: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 text-sm"
                  placeholder="e.g. Adjusted total to match pre-system quote"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={() => setShowRefundModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setRefundError(null);
                  const amt = parseFloat(refundForm.amount);
                  if (!Number.isFinite(amt) || amt <= 0) {
                    setRefundError('Amount must be > 0');
                    return;
                  }
                  setRefundSubmitting(true);
                  try {
                    await api.orders.issueRefund(order.id, {
                      amount: amt,
                      method: refundForm.method,
                      reference: refundForm.reference || undefined,
                      reason: refundForm.reason || undefined,
                    });
                    setShowRefundModal(false);
                    await loadOrder();
                  } catch (err: any) {
                    setRefundError(err.message || 'Failed to issue refund');
                  } finally {
                    setRefundSubmitting(false);
                  }
                }}
                disabled={refundSubmitting || !refundForm.amount}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
              >
                {refundSubmitting ? 'Issuing...' : 'Issue Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bin Selection Modal */}
      {showBinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Assign Bin to Order</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a bin for this order or skip to release without bin assignment.
            </p>

            {binsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : availableBins.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No available bins. You can still proceed without assigning a bin.
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto mb-4">
                <div className="space-y-2">
                  {availableBins.map((bin: any) => (
                    <label
                      key={bin.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedBinId === bin.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="bin"
                        value={bin.id}
                        checked={selectedBinId === bin.id}
                        onChange={() => setSelectedBinId(bin.id)}
                        className="mr-3"
                      />
                      <div>
                        <span className="font-medium">{bin.bin_number}</span>
                        {bin.location && (
                          <span className="text-gray-500 ml-2">({bin.location})</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowBinModal(false);
                  setSelectedBinId('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmRelease()}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Skip Bin
              </button>
              <button
                onClick={() => handleConfirmRelease(selectedBinId)}
                disabled={actionLoading || !selectedBinId}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Assign & Release'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Labels Modal */}

      {/* Parts-Order Edit Modal — admin edits qty/color/length per item */}
      {showPartsEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-1">Edit Parts — {order.orderNumber}</h3>
            <p className="text-xs text-gray-500 mb-4">Update qty, color, or length on existing line items. Re-quote freight separately if dimensions change.</p>
            {partsEditError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{partsEditError}</div>
            )}

            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Sidemark</label>
                  <input
                    type="text"
                    value={partsEditSidemark}
                    onChange={(e) => setPartsEditSidemark(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">PO Number</label>
                  <input
                    type="text"
                    value={partsEditPo}
                    onChange={(e) => setPartsEditPo(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {partsEditRows.map((row, idx) => (
                <div key={row.itemId} className="border rounded p-3 bg-gray-50">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-sm font-medium text-gray-800">Item #{row.itemNumber}: {row.name || '(unnamed part)'}</div>
                    <div className="text-xs text-gray-500">Unit: ${row.unitPrice.toFixed(2)} {row.unit}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">Unit Price ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unitPrice}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPartsEditRows((prev) => prev.map((r, i) => i === idx ? { ...r, unitPrice: v === '' ? 0 : Number(v) } : r));
                        }}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                        title="Override with the quoted price. Persists as the item's price (not recomputed from dimensions)."
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">Quantity</label>
                      <input
                        type="number"
                        min="0"
                        value={row.quantity}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPartsEditRows((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: v === '' ? 0 : Number(v) } : r));
                        }}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">Color</label>
                      <select
                        value={row.color || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPartsEditRows((prev) => prev.map((r, i) => i === idx ? { ...r, color: v || null } : r));
                        }}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      >
                        <option value="">— (no color) —</option>
                        <option value="black">Black</option>
                        <option value="white">White</option>
                        <option value="bronze">Bronze</option>
                        <option value="tan">Tan</option>
                        <option value="mill_finish">Mill Finish</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">Length (inches)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.125"
                        value={row.lengthInches ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPartsEditRows((prev) => prev.map((r, i) => i === idx ? { ...r, lengthInches: v === '' ? null : Number(v) } : r));
                        }}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                        disabled={row.lengthInches == null}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 text-right">
                    Line total: <span className="font-semibold text-gray-800">${((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0)).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t flex items-center justify-between">
              <div className="text-sm text-gray-700">
                New items subtotal: <span className="font-bold text-gray-900">${partsEditRows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unitPrice) || 0), 0).toFixed(2)}</span>
                <span className="text-xs text-gray-500 ml-2">(shipping unchanged: ${Number((order as any).shippingAmount || 0).toFixed(2)})</span>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowPartsEditModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm"
                disabled={partsEditSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePartsEdit}
                disabled={partsEditSaving}
                className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 text-sm"
              >
                {partsEditSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Order — {order.orderNumber}</h3>
            {editError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{editError}</div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO / Sidemark / Job Name</label>
                <input
                  type="text"
                  value={editForm.sidemark || ''}
                  onChange={(e) => setEditForm((f: any) => ({ ...f, sidemark: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Screen Location</label>
                <input
                  type="text"
                  value={editForm.poNumber || ''}
                  onChange={(e) => setEditForm((f: any) => ({ ...f, poNumber: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Needed-By Date</label>
                <input
                  type="date"
                  value={editForm.neededByDate || ''}
                  onChange={(e) => setEditForm((f: any) => ({ ...f, neededByDate: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Customer Info</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      type="text"
                      value={editForm.customerLeadName || ''}
                      onChange={(e) => setEditForm((f: any) => ({ ...f, customerLeadName: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={editForm.customerLeadEmail || ''}
                      onChange={(e) => setEditForm((f: any) => ({ ...f, customerLeadEmail: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editForm.customerLeadPhone || ''}
                      onChange={(e) => setEditForm((f: any) => ({ ...f, customerLeadPhone: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Address</label>
                    <input
                      type="text"
                      value={editForm.customerLeadAddress || ''}
                      onChange={(e) => setEditForm((f: any) => ({ ...f, customerLeadAddress: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm((f: any) => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Edit Modal */}
      {showItemEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Unit #{itemEditForm.itemNumber} — Measurements & Config</h3>
            {itemEditError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{itemEditError}</div>
            )}
            <div className="space-y-4">
              {/* Width measurements */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Width (inches)</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Top</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.widthTop}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBox({ ...f, widthTop: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Middle</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.widthMiddle}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBox({ ...f, widthMiddle: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Bottom</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.widthBottom}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBox({ ...f, widthBottom: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Height measurements */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Height (inches)</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Left</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.heightLeft}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBias({ ...f, heightLeft: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Center</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.heightCenter}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBias({ ...f, heightCenter: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Right</label>
                    <input
                      type="number"
                      step="0.0625"
                      value={itemEditForm.heightRight}
                      onChange={(e) => setItemEditForm((f: any) => withAutoBias({ ...f, heightRight: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Calculated preview */}
              {(() => {
                const wT = Number(itemEditForm.widthTop) || 0;
                const wM = Number(itemEditForm.widthMiddle) || 0;
                const wB = Number(itemEditForm.widthBottom) || 0;
                const hL = Number(itemEditForm.heightLeft) || 0;
                const hC = Number(itemEditForm.heightCenter) || 0;
                const hR = Number(itemEditForm.heightRight) || 0;
                const widths = [wT, wM, wB].filter(v => v > 0);
                const isInside = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(itemEditForm.trackMount || '');
                const wSel = widths.length > 0 ? (isInside ? Math.min(...widths) : Math.max(...widths)) : 0;
                const h = Math.max(hL, hC, hR);
                const tm = itemEditForm.trackMount || '';
                let tmAdj = 0;
                if (tm === 'wall') tmAdj = 6;
                else if (tm === 'inside_face_left' || tm === 'inside_face_right') tmAdj = 3;
                const hasUCh = itemEditForm.uChannel && itemEditForm.uChannel !== 'none';
                const uChDed = (hasUCh && itemEditForm.applyUChannelDeduction) ? (itemEditForm.uChannel === 'two_sides' ? 0.75 : 0.375) : 0;
                const sysW = wSel + tmAdj - uChDed;
                const boxDed = itemEditForm.applyBoxDeduction ? 0.25 : 0;
                const dBoxAdj = Number(itemEditForm.boxWidthAdjustment) || 0;
                const hasOverride = itemEditForm.boxWidthOverride !== '' && itemEditForm.boxWidthOverride != null;
                const computedBoxW = wSel + tmAdj - boxDed + dBoxAdj;
                const boxW = hasOverride ? Number(itemEditForm.boxWidthOverride) : computedBoxW;
                const rollerTube = boxW - uChDed - 14.5;
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-blue-700 mb-2">CALCULATED PREVIEW</h5>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-blue-600">System Width:</span>
                        <span className="ml-1 font-bold text-blue-800">{sysW.toFixed(3)}"</span>
                      </div>
                      <div>
                        <span className="text-blue-600">Box Width:</span>
                        <span className={`ml-1 font-bold ${hasOverride ? 'text-amber-700' : 'text-blue-800'}`}>{boxW.toFixed(3)}"</span>
                        {hasOverride && <span className="ml-1 text-[10px] text-amber-600">(override)</span>}
                      </div>
                      <div>
                        <span className="text-blue-600">Roller Tube:</span>
                        <span className="ml-1 font-bold text-blue-800">{rollerTube.toFixed(3)}"</span>
                      </div>
                      <div>
                        <span className="text-blue-600">Height:</span>
                        <span className="ml-1 font-bold text-blue-800">{h.toFixed(3)}"</span>
                      </div>
                    </div>
                    {hasOverride && (
                      <p className="mt-2 text-[11px] text-amber-700">
                        Box width overridden — drives housing + roller only. Fabric, tracks &amp; weight bar still use the system width ({sysW.toFixed(3)}") from the opening.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* System type + box-width override (factory manual controls) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">System Type</label>
                  <div className="inline-flex rounded-lg border overflow-hidden w-full">
                    <button
                      type="button"
                      onClick={() => setItemEditForm((f: any) => ({ ...f, systemType: 'box' }))}
                      className={`flex-1 px-3 py-2 text-sm font-medium ${itemEditForm.systemType !== 'open_roll' ? 'bg-amber-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Housing Box
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemEditForm((f: any) => ({ ...f, systemType: 'open_roll' }))}
                      className={`flex-1 px-3 py-2 text-sm font-medium border-l ${itemEditForm.systemType === 'open_roll' ? 'bg-amber-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Open Roll
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">Open Roll omits the housing box from the cut sheet.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Box Width Override (in)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.0625"
                      placeholder="auto (formula)"
                      value={itemEditForm.boxWidthOverride}
                      onChange={(e) => setItemEditForm((f: any) => ({ ...f, boxWidthOverride: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                    {itemEditForm.boxWidthOverride !== '' && itemEditForm.boxWidthOverride != null && (
                      <button
                        type="button"
                        onClick={() => setItemEditForm((f: any) => ({ ...f, boxWidthOverride: '' }))}
                        className="px-2 py-2 text-xs text-gray-500 hover:text-red-600 whitespace-nowrap"
                        title="Clear override — revert to formula"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">Absolute box width — bypasses the formula, drives housing + roller only. Leave blank to use openings.</p>
                </div>
              </div>

              {/* Advanced config toggle */}
              <button
                type="button"
                onClick={() => setShowItemEditAdvanced(!showItemEditAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <svg className={`w-4 h-4 transform transition-transform ${showItemEditAdvanced ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Advanced Configuration
              </button>

              {showItemEditAdvanced && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Motor Side</label>
                      <select
                        value={itemEditForm.motorSide}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, motorSide: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Track Mount</label>
                      <select
                        value={itemEditForm.trackMount}
                        onChange={(e) => setItemEditForm((f: any) => withAutoBox({ ...f, trackMount: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="face">Face Mount</option>
                        <option value="inside">Inside Mount</option>
                        <option value="wall">Wall Mount</option>
                        <option value="inside_face_left">Inside/Face Left</option>
                        <option value="inside_face_right">Inside/Face Right</option>
                        <option value="recessed">Recessed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">U-Channel</label>
                      <select
                        value={itemEditForm.uChannel}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, uChannel: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="none">None</option>
                        <option value="one_side">One Side</option>
                        <option value="two_sides">Two Sides</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4 pt-5">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={itemEditForm.applyUChannelDeduction || false}
                          onChange={(e) => setItemEditForm((f: any) => ({ ...f, applyUChannelDeduction: e.target.checked }))}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                        Apply U-Ch Deductions
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Box Width Adjustment</label>
                      <input
                        type="number"
                        step="0.0625"
                        min="-0.25"
                        max="0.75"
                        value={itemEditForm.boxWidthAdjustment}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, boxWidthAdjustment: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div className="flex items-center gap-4 pt-5">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={itemEditForm.applyBoxDeduction ?? true}
                          onChange={(e) => setItemEditForm((f: any) => ({ ...f, applyBoxDeduction: e.target.checked }))}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                        Apply Box Deduction (-0.25")
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fabric Type</label>
                      <select
                        value={itemEditForm.fabricType}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const colors = fabricColorMap[newType] || [];
                          setItemEditForm((f: any) => ({ ...f, fabricType: newType, fabricColor: colors[0] || '' }));
                        }}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">—</option>
                        <option value="omega_pro_3">Omega Pro 3%</option>
                        <option value="omega_pro_5">Omega Pro 5%</option>
                        <option value="omega_pro_10">Omega Pro 10%</option>
                        <option value="twitchell_80">Twitchell 80</option>
                        <option value="twitchell_90">Twitchell 90</option>
                        <option value="twitchell_95">Twitchell 95</option>
                        <option value="twitchell_nano_95">Twitchell Nano 95</option>
                        <option value="twitchell_nano_97">Twitchell Nano 97</option>
                        <option value="twitchell_nano_99">Twitchell Nano 99</option>
                        <option value="twitchell_dimout_blackout">Twitchell Dimout/Blackout</option>
                        <option value="502_vinyl">502 Vinyl</option>
                        <option value="twitchell_nano_55_insect">Twitchell Nano 55 Insect</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fabric Color</label>
                      <select
                        value={itemEditForm.fabricColor}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, fabricColor: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        {(fabricColorMap[itemEditForm.fabricType] || []).map((color: string) => (
                          <option key={color} value={color}>{color}</option>
                        ))}
                        {!(fabricColorMap[itemEditForm.fabricType] || []).length && itemEditForm.fabricColor && (
                          <option value={itemEditForm.fabricColor}>{itemEditForm.fabricColor}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Motor Type</label>
                      <select
                        value={itemEditForm.motorType}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, motorType: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">—</option>
                        <option value="keder">Keder</option>
                        <option value="alpha">Alpha</option>
                        <option value="somfy">Somfy</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Frame Color</label>
                      <select
                        value={itemEditForm.systemColor}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, systemColor: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">—</option>
                        <option value="black">Black</option>
                        <option value="white">White</option>
                        <option value="bronze">Bronze</option>
                        <option value="tan">Tan</option>
                        <option value="mill_finish">Mill Finish</option>
                        <option value="custom">Custom RAL</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Brush Size</label>
                      <select
                        value={itemEditForm.brushSize}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, brushSize: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="1inch">1 inch</option>
                        <option value="2inch">2 inch</option>
                      </select>
                    </div>
                  </div>

                  {/* Bias cut (angled top — one side taller) */}
                  <div className="border-t pt-4">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={itemEditForm.biasedCut || false}
                        onChange={(e) => setItemEditForm((f: any) => withAutoBias({ ...f, biasedCut: e.target.checked }))}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      Bias Cut (angled bottom)
                    </label>
                    {itemEditForm.biasedCut && (() => {
                      const auto = calcRecommendedBias(
                        Number(itemEditForm.heightLeft) || 0,
                        Number(itemEditForm.heightCenter) || 0,
                        Number(itemEditForm.heightRight) || 0,
                      );
                      return (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Longer Side</label>
                          <select
                            value={itemEditForm.biasedCutSide || 'left'}
                            onChange={(e) => setItemEditForm((f: any) => ({ ...f, biasedCutSide: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Bias Amount (in)</label>
                          <input
                            type="number"
                            step="0.0625"
                            min="0"
                            max="4"
                            value={itemEditForm.biasedCutInches}
                            onChange={(e) => setItemEditForm((f: any) => ({ ...f, biasedCutInches: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div className="col-span-2 flex items-center justify-between">
                          <p className="text-[11px] text-gray-400">
                            Auto-calculated from the heights ({auto.needsBias ? `${auto.longerSide} +${auto.biasAmount}"` : 'no bias — heights within 0.25"'}). Angled bottom, {itemEditForm.biasedCutSide === 'right' ? 'right' : 'left'} side longer. Shows on the material label (e.g. "{(itemEditForm.biasedCutSide || 'left').toUpperCase()} {itemEditForm.biasedCutInches || 0}"").
                          </p>
                          {auto.needsBias && (Number(itemEditForm.biasedCutInches) !== auto.biasAmount || itemEditForm.biasedCutSide !== auto.longerSide) && (
                            <button
                              type="button"
                              onClick={() => setItemEditForm((f: any) => ({ ...f, biasedCutInches: auto.biasAmount, biasedCutSide: auto.longerSide }))}
                              className="ml-2 shrink-0 text-[11px] font-medium text-amber-600 hover:text-amber-700 whitespace-nowrap"
                              title="Reset to the value auto-calculated from the heights"
                            >
                              Use auto
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })()}
                  </div>

                  {/* Screw covers (snap over exposed face-mount track screws) */}
                  <div className="border-t pt-4">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={itemEditForm.screwCovers || false}
                        onChange={(e) => setItemEditForm((f: any) => ({ ...f, screwCovers: e.target.checked, screwCoverQty: e.target.checked ? (f.screwCoverQty || 2) : 0 }))}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      Screw Covers
                    </label>
                    {itemEditForm.screwCovers && (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Tracks</label>
                          <select
                            value={itemEditForm.screwCoverQty || 2}
                            onChange={(e) => setItemEditForm((f: any) => ({ ...f, screwCoverQty: Number(e.target.value) }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                          >
                            <option value={1}>1 track</option>
                            <option value={2}>2 tracks</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <p className="text-[11px] text-gray-400">
                            Priced per track by height. The charge line updates when you save.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Item Notes</label>
                    <textarea
                      value={itemEditForm.notes}
                      onChange={(e) => setItemEditForm((f: any) => ({ ...f, notes: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowItemEditModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveItemEdit}
                disabled={itemEditSaving}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {itemEditSaving ? 'Saving...' : 'Save Item Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLabelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Print Labels</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Queue and print labels for all work orders in this order.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label Type</label>
                <select
                  value={labelType}
                  onChange={(e) => setLabelType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="material">Material</option>
                  <option value="track">Track</option>
                  <option value="housing">Housing</option>
                  <option value="roll_tube">Roll Tube</option>
                  <option value="bottom_bar">Bottom Bar</option>
                  <option value="final_product">Final Product</option>
                  <option value="shipping">Shipping</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Copies</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={labelCopies}
                  onChange={(e) => setLabelCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Printer</label>
                {labelPrinters.length > 0 ? (
                  <select
                    value={selectedPrinterId}
                    onChange={(e) => setSelectedPrinterId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {labelPrinters.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.isDefault ? '(Default)' : ''} {!p.isOnline ? '(Offline)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">No printers configured. Labels will be queued only.</p>
                )}
              </div>

              {labelResults.length > 0 && (
                <div className="border rounded-lg p-3 dark:border-gray-600 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Results:</p>
                  {labelResults.map((r, i) => (
                    <div key={i} className={`text-sm py-1 ${r.status === 'printed' ? 'text-green-600' : r.status === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {r.status === 'printed' ? '\u2713' : r.status === 'error' ? '\u2717' : '\u25CB'} {r.message || r.status}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowLabelModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
              >
                Close
              </button>
              <button
                onClick={handlePrintLabels}
                disabled={labelPrinting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {labelPrinting ? 'Printing...' : 'Print Labels'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CUT SHEET TAB
// ============================================================================

function CutSheetTab({ cutSheet, loading, order }: { cutSheet: any; loading: boolean; order: OrderData }) {
  const [selectedUnit, setSelectedUnit] = useState<number | 'all'>('all');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600">Loading cut sheet...</span>
      </div>
    );
  }

  if (!cutSheet || !cutSheet.items || cutSheet.items.length === 0) {
    return (
      <Card title="Cut Sheet">
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500">No cut sheet data available.</p>
          <p className="text-sm text-gray-400 mt-1">Cut sheet will be generated when order is pushed to production.</p>
        </div>
      </Card>
    );
  }

  const visibleItems = selectedUnit === 'all'
    ? cutSheet.items
    : cutSheet.items.filter((_: any, i: number) => i === (selectedUnit as number));

  return (
    <div className="space-y-6">
      {/* Summary + Unit Selector */}
      <Card title={`Cut Sheet - ${order.orderNumber}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-500">Total Units</p>
            <p className="font-semibold text-lg">{cutSheet.items.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Order Status</p>
            <StatusBadge status={order.status} type="order" />
          </div>
          {cutSheet.generatedAt && (
            <div>
              <p className="text-sm text-gray-500">Generated</p>
              <p className="font-medium">{format(new Date(cutSheet.generatedAt), 'MMM d, yyyy h:mm a')}</p>
            </div>
          )}
        </div>

        {/* Unit picker — only show if more than 1 unit */}
        {cutSheet.items.length > 1 && (
          <div className="flex flex-wrap gap-2 pt-3 border-t dark:border-gray-700">
            <button
              onClick={() => setSelectedUnit('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedUnit === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Units
            </button>
            {cutSheet.items.map((item: any, index: number) => (
              <button
                key={index}
                onClick={() => setSelectedUnit(index)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedUnit === index
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Unit #{item.itemNumber || index + 1}
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Cut Details by Unit */}
      {visibleItems.map((item: any, index: number) => (
        <Card key={item.orderItemId || index} title={`Unit #${item.itemNumber || index + 1}`}>
          <div className="space-y-4">
            {/* Dimensions */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">SYSTEM DIMENSIONS</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">System Width:</span>
                  <span className="ml-2 font-semibold text-blue-700">{item.calculatedDimensions?.systemWidth?.toFixed(3) || '-'}"</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">System Height:</span>
                  <span className="ml-2 font-medium">{item.calculatedDimensions?.systemHeight?.toFixed(3) || '-'}"</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Box Width:</span>
                  <span className="ml-2 font-semibold text-indigo-700">
                    {item.calculatedDimensions
                      ? ((item.calculatedDimensions.widthSelected || 0)
                        + (item.calculatedDimensions.boxWidthAdjustment || 0)
                        - (item.calculatedDimensions.boxDeduction || 0)
                        + (item.calculatedDimensions.dealerBoxAdjustment || 0)).toFixed(3)
                      : '-'}"
                  </span>
                </div>
                {item.calculatedDimensions?.uChannelDeduction > 0 && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">U-Channel Ded:</span>
                    <span className="ml-2 font-medium text-orange-600">-{item.calculatedDimensions.uChannelDeduction}"</span>
                  </div>
                )}
                {item.calculatedDimensions?.dealerBoxAdjustment !== 0 && item.calculatedDimensions?.dealerBoxAdjustment != null && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Dealer Box Adj:</span>
                    <span className="ml-2 font-medium text-orange-600">
                      {item.calculatedDimensions.dealerBoxAdjustment > 0 ? '+' : ''}{item.calculatedDimensions.dealerBoxAdjustment}"
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Cut Sizes */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-3">CUT SIZES</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {item.cutSizes?.rollerTube != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">Roller Tube</span>
                    <span className="font-bold text-lg">{item.cutSizes.rollerTube.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.housingMain != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">Housing Main</span>
                    <span className="font-bold text-lg">{item.cutSizes.housingMain.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.housingCover != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">Housing Cover</span>
                    <span className="font-bold text-lg">{item.cutSizes.housingCover.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.vTrack != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">V-Track</span>
                    <span className="font-bold text-lg">{item.cutSizes.vTrack.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.weightBar != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">Weight Bar</span>
                    <span className="font-bold text-lg">{item.cutSizes.weightBar.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.uChannel != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border border-blue-300">
                    <span className="text-blue-600 block text-xs">U-Channel</span>
                    <span className="font-bold text-lg">{item.cutSizes.uChannel.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.fabricWidth != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border border-green-300">
                    <span className="text-green-600 block text-xs">Fabric Width</span>
                    <span className="font-bold text-lg">{item.cutSizes.fabricWidth.toFixed(3)}"</span>
                  </div>
                )}
                {item.cutSizes?.fabricHeight != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border border-green-300">
                    <span className="text-green-600 block text-xs">Fabric Height</span>
                    <span className="font-bold text-lg">{item.cutSizes.fabricHeight.toFixed(3)}"</span>
                  </div>
                )}
              </div>

              {/* Drop Allocations */}
              {item.dropAllocations && item.dropAllocations.length > 0 && (
                <div className="mt-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300 uppercase mb-2">Use Existing Drops</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {item.dropAllocations.map((drop: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-gray-800 rounded p-2 border border-emerald-200 dark:border-emerald-800">
                        <div className="font-bold text-emerald-700 dark:text-emerald-300 text-sm">{drop.dropId}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {(drop.componentType || '').replace(/_/g, ' ')} | BIN: {drop.dropBinLocation || 'N/A'} | {drop.dropLength}" avail | Cut to: {drop.cutLength}"
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Spring Holes */}
            {item.springHoles && (
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-3">SPRING HOLE POSITIONS</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Holes / Track:</span>
                    <span className="ml-2 font-bold text-lg">{item.springHoles.holes}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Spacing:</span>
                    <span className="ml-2 font-medium">{item.springHoles.spacing?.toFixed(2) || '-'}"</span>
                  </div>
                  {/* Spring QUANTITY — holes are drill positions per track; the
                      BOM bills holes * 4 (parts 44401 / 44444.4). Showing only
                      holes meant the count had to be inferred at the bench. */}
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Springs (unit):</span>
                    <span className="ml-2 font-bold text-lg text-purple-700 dark:text-purple-300">
                      {(item.springHoles as any).springs ?? (item.springHoles.holes || 0) * 4}
                    </span>
                  </div>
                </div>
                {item.springHoles.positions && item.springHoles.positions.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 border">
                    <span className="text-gray-500 dark:text-gray-400 block text-xs mb-2">Positions from Top:</span>
                    <div className="flex flex-wrap gap-2">
                      {item.springHoles.positions.map((pos: number, i: number) => (
                        <span key={i} className="px-3 py-1 bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full font-mono text-sm">
                          {pos.toFixed(2)}"
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Seam Info */}
            {item.seamRequired && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">SEAM REQUIRED</h4>
                {item.seamDetails && (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Panel 1:</span>
                      <span className="ml-2 font-medium">{item.seamDetails.panel1Height?.toFixed(2) || '-'}"</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Panel 2:</span>
                      <span className="ml-2 font-medium">{item.seamDetails.panel2Height?.toFixed(2) || '-'}"</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Total:</span>
                      <span className="ml-2 font-medium">{item.seamDetails.totalHeight?.toFixed(2) || '-'}"</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Warnings */}
            {item.warnings && item.warnings.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">WARNINGS</h4>
                <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400">
                  {item.warnings.map((warning: string, i: number) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// BOM TAB
// ============================================================================

function BomTab({ bom, loading, order }: { bom: any[]; loading: boolean; order: OrderData }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600">Loading BOM...</span>
      </div>
    );
  }

  // Build per-unit data with BOM items
  const unitData: { itemNumber: number; orderItem?: OrderItem; bomItems: any[] }[] = [];
  bom.forEach((unitBom: any, unitIndex: number) => {
    const itemNum = unitBom.itemNumber || unitIndex + 1;
    const items = unitBom.bomItems || unitBom.items || [];
    const orderItem = order.items?.find(i => i.itemNumber === itemNum);
    unitData.push({ itemNumber: itemNum, orderItem, bomItems: items });
  });

  // Flatten for totals
  const allBomItems: any[] = [];
  unitData.forEach(unit => {
    unit.bomItems.forEach((item: any) => {
      allBomItems.push({ ...item, unitNumber: unit.itemNumber });
    });
  });

  if (allBomItems.length === 0) {
    return (
      <Card title="Bill of Materials">
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-gray-500">No BOM data available.</p>
          <p className="text-sm text-gray-400 mt-1">BOM will be generated when order is pushed to production.</p>
        </div>
      </Card>
    );
  }

  // Calculate totals
  const totalParts = allBomItems.length;
  const allocatedParts = allBomItems.filter(i => i.allocated).length;
  const grandTotalCost = allBomItems.reduce((sum, i) => sum + (parseFloat(i.totalCost) || 0), 0);
  const fmtMoney = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Helper to format dimensions
  const formatDims = (item: OrderItem) => {
    const w = item.maxWidth || Math.max(item.widthTop || 0, item.widthMiddle || 0, item.widthBottom || 0);
    const h = item.maxHeight || Math.max(item.heightLeft || 0, item.heightCenter || 0, item.heightRight || 0);
    const wFt = Math.floor(w / 12);
    const wIn = w % 12;
    const hFt = Math.floor(h / 12);
    const hIn = h % 12;
    return `${wFt}'${wIn > 0 ? `${wIn}"` : ''} x ${hFt}'${hIn > 0 ? `${hIn}"` : ''} (${w}" x ${h}")`;
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card title={`Bill of Materials - ${order.orderNumber}`}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <p className="text-sm text-gray-500">Total Parts</p>
            <p className="font-semibold text-lg">{totalParts}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Allocated</p>
            <p className="font-semibold text-lg text-green-600">{allocatedParts}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Pending</p>
            <p className="font-semibold text-lg text-yellow-600">{totalParts - allocatedParts}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Screens</p>
            <p className="font-semibold text-lg">{unitData.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Material Cost</p>
            <p className="font-semibold text-lg text-blue-600">{fmtMoney(grandTotalCost)}</p>
          </div>
        </div>
      </Card>

      {/* BOM per Screen */}
      {unitData.map((unit) => {
        const oi = unit.orderItem;
        // Group this unit's BOM items by component type
        const grouped = unit.bomItems.reduce((acc: Record<string, any[]>, item: any) => {
          const type = item.componentType || item.workstation || 'Other';
          if (!acc[type]) acc[type] = [];
          acc[type].push(item);
          return acc;
        }, {});
        const unitAllocated = unit.bomItems.filter((i: any) => i.allocated).length;
        const unitTotalCost = unit.bomItems.reduce((s: number, i: any) => s + (parseFloat(i.totalCost) || 0), 0);

        return (
          <Card
            key={unit.itemNumber}
            title={`Screen #${unit.itemNumber}${oi ? ` - ${formatFabricType(oi.fabricType)} ${oi.fabricColor || ''}` : ''}${unitTotalCost > 0 ? `  ·  ${fmtMoney(unitTotalCost)} material` : ''}`}
          >
            {/* Screen info header */}
            {oi && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Dimensions:</span>
                    <span className="ml-1 font-medium">{formatDims(oi)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Motor:</span>
                    <span className="ml-1 font-medium">{oi.motorSide} / {motorLabel(oi.motorType) || 'Keder'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Frame:</span>
                    <span className="ml-1 font-medium">{oi.systemColor || 'Black'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Parts:</span>
                    <span className="ml-1 font-medium">{unit.bomItems.length} ({unitAllocated} allocated)</span>
                  </div>
                </div>
                {/* Component Colors — show when mixed colors exist */}
                {oi.componentColors && Object.keys(oi.componentColors).length > 0 && (() => {
                  const cc = oi.componentColors!;
                  const fmt = (color: string, ralKey: keyof typeof cc) => {
                    if (color === 'custom' && cc[ralKey]) return `Custom (${cc[ralKey]})`;
                    return color.charAt(0).toUpperCase() + color.slice(1);
                  };
                  return (
                  <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-600 rounded text-sm">
                    <span className="font-semibold text-orange-600 dark:text-orange-400">Mixed Colors: </span>
                    {cc.housingColor && (
                      <span className="mr-3">Box={fmt(cc.housingColor, 'housingRalNumber')}</span>
                    )}
                    {cc.leftTrackColor && (
                      <span className="mr-3">L.Track={fmt(cc.leftTrackColor, 'leftTrackRalNumber')}</span>
                    )}
                    {cc.rightTrackColor && (
                      <span className="mr-3">R.Track={fmt(cc.rightTrackColor, 'rightTrackRalNumber')}</span>
                    )}
                    {cc.weightBarColor && (
                      <span className="mr-3">WBar={fmt(cc.weightBarColor, 'weightBarRalNumber')}</span>
                    )}
                    {cc.leftUChannelColor && (
                      <span className="mr-3">L.UCh={fmt(cc.leftUChannelColor, 'leftUChannelRalNumber')}</span>
                    )}
                    {cc.rightUChannelColor && (
                      <span className="mr-3">R.UCh={fmt(cc.rightUChannelColor, 'rightUChannelRalNumber')}</span>
                    )}
                  </div>
                  );
                })()}
              </div>
            )}

            {/* Parts grouped by component type */}
            <div className="space-y-4">
              {Object.entries(grouped).map(([type, items]) => (
                <div key={type}>
                  <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1 px-1">
                    {formatComponentType(type)} ({items.length})
                  </h5>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Cut Length</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                          <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {items.map((item: any, index: number) => {
                          const itemCost = parseFloat(item.unitCost) || 0;
                          const itemTotal = parseFloat(item.totalCost) || 0;
                          return (
                          <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-3 py-1.5 text-sm font-mono">{item.partNumber || '-'}</td>
                            <td className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400">{item.description || '-'}</td>
                            <td className="px-3 py-1.5 text-sm text-right font-medium">
                              {item.quantity} {item.unit || 'ea'}
                            </td>
                            <td className="px-3 py-1.5 text-sm text-right font-mono">
                              {item.cutLength ? `${parseFloat(item.cutLength).toFixed(3)}"` : '-'}
                            </td>
                            <td className="px-3 py-1.5 text-sm text-right font-mono text-gray-600 dark:text-gray-400">
                              {itemCost > 0 ? `$${itemCost.toFixed(itemCost < 1 ? 4 : 2)}` : '-'}
                            </td>
                            <td className="px-3 py-1.5 text-sm text-right font-mono font-medium">
                              {itemTotal > 0 ? `$${itemTotal.toFixed(2)}` : '-'}
                            </td>
                            <td className="px-3 py-1.5 text-xs text-gray-500">{item.workstation || '-'}</td>
                            <td className="px-3 py-1.5 text-center">
                              {item.consumed ? (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Used</span>
                              ) : item.allocated ? (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Allocated</span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pending</span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Cost Analysis Tab Component
function CostAnalysisTab({
  costData,
  comparison,
  loading,
  orderId,
  freightCost = 0,
  onRecalculate,
}: {
  costData: any;
  comparison: any;
  loading: boolean;
  orderId: string;
  freightCost?: number;
  onRecalculate: () => void;
}) {
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.costAccounting.recalculateCostSnapshots(orderId);
      onRecalculate();
    } catch (err) {
      console.error('Recalculate failed:', err);
    } finally {
      setRecalculating(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (!costData || !costData.items || costData.items.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 mx-auto text-yellow-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-medium text-yellow-800 mb-2">No Cost Data Available</h3>
        <p className="text-yellow-700 mb-4">Cost analysis data is captured when units complete production.</p>
        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {recalculating ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Recalculating...</>
          ) : (
            <>Recalculate Cost Data</>
          )}
        </button>
      </div>
    );
  }

  const { items, totals } = costData;

  return (
    <div className="space-y-6">
      {/* Comparison Banner */}
      {comparison && (
        <div className={`rounded-lg p-4 ${
          comparison.margin_diff >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Order Margin vs 90-Day Average</p>
              <p className={`text-2xl font-bold ${comparison.margin_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {comparison.margin_diff >= 0 ? '+' : ''}{comparison.margin_diff.toFixed(1)}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">This Order: {comparison.order_avg_margin.toFixed(1)}%</p>
              <p className="text-sm text-gray-500">90-Day Avg: {comparison.overall_avg_margin.toFixed(1)}%</p>
              <p className="text-xs text-gray-400 mt-1">
                Percentile: Top {100 - comparison.percentile}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order Totals — freight COGS (from consolidated shipment) folded in */}
      {(() => {
        // Commission is a cost of the sale — fold it in alongside freight so
        // "true cost" is what the order actually costs us to win and build.
        const commissionCost = Number((totals as any).commission_cost || 0);
        const trueCost = (totals.true_cost || 0) + freightCost + commissionCost;
        const marginDollars = (totals.revenue || 0) - trueCost;
        const marginPercent = totals.revenue > 0 ? (marginDollars / totals.revenue) * 100 : 0;
        return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Material</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.material_cost)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Labor</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.labor_cost)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Overhead</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.overhead)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Freight</p>
              <p className="text-lg font-semibold text-gray-900">{freightCost > 0 ? formatCurrency(freightCost) : '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Commission</p>
              <p className="text-lg font-semibold text-gray-900">{commissionCost > 0 ? formatCurrency(commissionCost) : '—'}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 uppercase">True Cost</p>
              <p className="text-lg font-bold text-blue-700">{formatCurrency(trueCost)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Revenue</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.revenue)}</p>
            </div>
            <div className={`rounded-lg p-3 ${marginDollars >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className={`text-xs uppercase ${marginDollars >= 0 ? 'text-green-600' : 'text-red-600'}`}>Margin $</p>
              <p className={`text-lg font-bold ${marginDollars >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(marginDollars)}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${marginPercent >= 25 ? 'bg-green-50' : marginPercent >= 15 ? 'bg-yellow-50' : 'bg-red-50'}`}>
              <p className={`text-xs uppercase ${marginPercent >= 25 ? 'text-green-600' : marginPercent >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>Margin %</p>
              <p className={`text-lg font-bold ${marginPercent >= 25 ? 'text-green-700' : marginPercent >= 15 ? 'text-yellow-700' : 'text-red-700'}`}>
                {marginPercent.toFixed(1)}%
              </p>
            </div>
          </div>
          {freightCost > 0 && (
            <p className="text-xs text-gray-400 mt-2">Freight is this order's share of a consolidated shipment's carrier cost.</p>
          )}
          {Array.isArray(costData?.commissions) && costData.commissions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase mb-2">Commission Breakdown</p>
              <div className="space-y-1">
                {costData.commissions.map((c: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">
                      {c.payee}
                      <span className="text-gray-400 ml-2">
                        {String(c.level || '').replace(/_/g, ' ')} · {c.percent}%
                      </span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                        c.status === 'paid' ? 'bg-green-100 text-green-700'
                        : c.status === 'earned' ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                    </span>
                    <span className="font-medium text-gray-900">{formatCurrency(c.amount)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Commission is counted as a cost of the sale. Includes pending, earned and paid; clawed-back rows are excluded.
              </p>
            </div>
          )}
        </div>
        );
      })()}

      {/* Per-Unit Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Per-Unit Cost Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Material</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Labor Hrs</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Labor $</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overhead</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Freight</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Comm.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">True Cost</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin $</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Margin %</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Flags</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item: any, index: number) => {
                // Freight and commission are ORDER-level. Allocate each unit its
                // share by revenue so a per-unit margin means the same thing as
                // the order margin above it — the table used to show material
                // only, so a unit could read 85.5% on a page whose Order Summary
                // said 68.9%.
                const orderRevenue = items.reduce(
                  (a: number, r: any) => a + (Number(r.selling_price) || 0), 0);
                const share = orderRevenue > 0
                  ? (Number(item.selling_price) || 0) / orderRevenue
                  : (items.length ? 1 / items.length : 0);
                const unitFreight = freightCost * share;
                // commission is order-level (from totals) — same source the Order
                // Summary uses; it isn't in scope here, so read it directly.
                const unitCommission = (Number((totals as any).commission_cost) || 0) * share;
                const unitTrueCost = (Number(item.true_cost) || 0) + unitFreight + unitCommission;
                const unitRevenue = Number(item.selling_price) || 0;
                const unitMarginDollars = unitRevenue - unitTrueCost;
                const unitMarginPercent = unitRevenue > 0
                  ? (unitMarginDollars / unitRevenue) * 100 : 0;
                return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{item.unit_number || index + 1}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {item.width}" x {item.height}"
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {formatCurrency(Number(item.material_cost) || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {(Number(item.labor_hours) || 0).toFixed(2)}h
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {formatCurrency(Number(item.labor_cost) || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {formatCurrency(Number(item.overhead_allocated) || 0)}
                    {item.overhead_is_estimated && (
                      <span className="ml-1 text-xs text-yellow-600" title="Estimated">*</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {unitFreight > 0 ? formatCurrency(unitFreight) : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {unitCommission > 0 ? formatCurrency(unitCommission) : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(unitTrueCost)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                    {formatCurrency(unitRevenue)}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                    unitMarginDollars >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(unitMarginDollars)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      unitMarginPercent >= 25
                        ? 'bg-green-100 text-green-800'
                        : unitMarginPercent >= 15
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {unitMarginPercent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <div className="flex gap-1 justify-center">
                      {item.has_rework && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          Rework
                        </span>
                      )}
                      {item.is_scrapped && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Scrapped
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {items.some((i: any) => i.overhead_is_estimated) && (
          <p className="mt-3 text-xs text-yellow-600">
            * Overhead marked with asterisk is estimated from previous month. Final values will be calculated at month close.
          </p>
        )}
      </div>
    </div>
  );
}

function formatComponentType(type: string): string {
  const map: Record<string, string> = {
    'housing': 'Housing Components',
    'roller': 'Roller Assembly',
    'track': 'Track Components',
    'fabric': 'Fabric',
    'weight_bar': 'Weight Bar',
    'u_channel': 'U-Channel',
    'motor': 'Motor Components',
    'hardware': 'Hardware',
    'accessories': 'Accessories',
  };
  return map[type.toLowerCase()] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Screen Unit Visual Preview Component
function ScreenUnitPreview({ item }: { item: OrderItem }) {
  // Inside mount uses smallest width, face mount uses largest
  const isInsideMount = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(item.trackMount || '');
  const widthValues = [item.widthTop, item.widthMiddle, item.widthBottom].filter((v): v is number => v != null && v > 0);
  const primaryWidth = widthValues.length > 0
    ? (isInsideMount ? Math.min(...widthValues) : Math.max(...widthValues))
    : (item.maxWidth || 0);
  const maxWidth = primaryWidth;
  const maxHeight = item.maxHeight || Math.max(item.heightLeft || 0, item.heightCenter || 0, item.heightRight || 0);

  if (maxWidth === 0 || maxHeight === 0) return null;

  const maxDisplayWidth = 200;
  const maxDisplayHeight = 140;
  const scale = Math.min(maxDisplayWidth / maxWidth, maxDisplayHeight / maxHeight, 1.5);

  const displayWidth = maxWidth * scale;
  const displayHeight = maxHeight * scale;

  const housingHeight = 10;
  const weightBarHeight = 8;
  // Track width: face mount = narrower (3), inside/recessed/hybrid = wider (6)
  const trackWidth = item.trackMount === 'face' ? 3 : 6;

  const biasedCut = item.biasedCut;
  const biasAmount = item.biasedCutInches || 0;
  // biasedCutSide stores which side is LONGER. Use it as the source of truth;
  // heights are often equal so the height comparison alone is unreliable.
  const leftIsHigher = item.biasedCutSide
    ? item.biasedCutSide === 'left'
    : (item.heightLeft || 0) > (item.heightRight || 0);

  const hasLeftUChannel = item.uChannel === 'both_sides' || item.uChannel === 'left_side' || item.uChannel === 'two_sides';
  const hasRightUChannel = item.uChannel === 'both_sides' || item.uChannel === 'right_side' || item.uChannel === 'two_sides';

  // Window panel data (rendered as overlay rectangles inside the fabric area).
  // Designer terminology: Horizontal = ROWS (windows stacked top-to-bottom),
  // Vertical = COLUMNS (windows side-by-side). dividerType 'weld' = no gap;
  // 'divider' = fabric strip of `dividerSize` inches between windows.
  const hasWindow = (item as any).windowPanel || (item as any).window_panel;
  const wd: any = (item as any).windowData || (item as any).window_data || {};
  const wOrient = (wd.orientation || 'horizontal') as 'horizontal' | 'vertical' | 'grid';
  const wCount = parseInt(wd.windowCount, 10) || 1;
  const wTopBorder = parseFloat(wd.borderTop) || 4;
  const wSideBorder = parseFloat(wd.borderSide) || 4;
  const wBottomBorder = parseFloat(wd.borderBottom) || 4;
  const wDividerType = wd.dividerType || 'weld';
  const wDividerSize = wDividerType === 'divider' ? (parseFloat(wd.dividerSize) || 0) : 0;

  return (
    <div className="bg-gray-50 border rounded-lg p-3">
      <h5 className="text-xs font-semibold text-gray-600 mb-2">VISUAL PREVIEW</h5>
      <div className="flex justify-center">
        <div className="relative" style={{ width: displayWidth + 30, height: displayHeight + 40 }}>
          <div className="absolute top-0 text-center text-[9px] text-gray-500" style={{ left: 15, right: 15 }}>
            {maxWidth}"
          </div>
          <div className="absolute right-0 flex items-center text-[9px] text-gray-500" style={{ top: 15, height: displayHeight }}>
            <span className="transform rotate-90">{maxHeight}"</span>
          </div>

          <div
            className="absolute border border-gray-400 bg-white"
            style={{ left: 15, top: 15, width: displayWidth, height: displayHeight }}
          >
            <div
              className="absolute top-0 left-0 right-0 bg-gray-400 flex items-center justify-center"
              style={{ height: housingHeight }}
            >
              <span className="text-[6px] text-white font-medium">HOUSING</span>
            </div>

            <div
              className="absolute left-0 bg-gray-300"
              style={{
                top: housingHeight,
                width: trackWidth,
                height: displayHeight - housingHeight - weightBarHeight,
                borderLeft: hasLeftUChannel ? '2px solid #3b82f6' : 'none'
              }}
            />

            <div
              className="absolute right-0 bg-gray-300"
              style={{
                top: housingHeight,
                width: trackWidth,
                height: displayHeight - housingHeight - weightBarHeight,
                borderRight: hasRightUChannel ? '2px solid #3b82f6' : 'none'
              }}
            />

            <div
              className="absolute bg-green-100 border border-green-300"
              style={{
                left: trackWidth,
                right: trackWidth,
                top: housingHeight,
                bottom: weightBarHeight,
              }}
            >
              {hasWindow ? (() => {
                const fabricInnerW = displayWidth - trackWidth * 2;
                const fabricInnerH = displayHeight - housingHeight - weightBarHeight;
                const sb = wSideBorder * scale;
                const tb = wTopBorder * scale;
                const bb = wBottomBorder * scale;
                const dv = wDividerSize * scale;
                const winAreaW = Math.max(0, fabricInnerW - sb * 2);
                const winAreaH = Math.max(0, fabricInnerH - tb - bb);

                // Designer behavior (NewOrderPage WindowDesigner ~line 791): both
                // horizontal and vertical produce side-by-side windows in a single row
                // — the "orientation" label only changes the per-pane max dimensions
                // (horizontal pane = 106"W × 52"H, vertical pane = 52"W × 106"H).
                // Only `grid` is 2D, using horizontal/verticalDividers.
                let cols = 1, rows = 1;
                if (wOrient === 'horizontal' || wOrient === 'vertical') {
                  cols = Math.max(1, wCount);
                  rows = 1;
                } else if (wOrient === 'grid') {
                  const hd = (parseInt(wd.horizontalDividers, 10) || 0);
                  const vd = (parseInt(wd.verticalDividers, 10) || 0);
                  rows = Math.max(1, hd + 1);
                  cols = Math.max(1, vd + 1);
                }

                const winW = (winAreaW - dv * (cols - 1)) / cols;
                const winH = (winAreaH - dv * (rows - 1)) / rows;

                const cells: React.ReactNode[] = [];
                let idx = 0;
                for (let r = 0; r < rows; r++) {
                  for (let c = 0; c < cols; c++) {
                    if (idx >= wCount) break;
                    const x = sb + c * (winW + dv);
                    const y = tb + r * (winH + dv);
                    cells.push(
                      <div
                        key={`win-${r}-${c}`}
                        className="absolute bg-blue-100 border border-blue-400"
                        style={{ left: x, top: y, width: winW, height: winH }}
                      />
                    );
                    idx++;
                  }
                }
                return (
                  <>
                    {cells}
                    <div className="absolute top-0.5 left-1 text-[6px] text-blue-700 font-medium">
                      {wCount}W {wDividerType === 'weld' ? 'WLD' : `${wDividerSize}"D`}
                    </div>
                  </>
                );
              })() : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[8px] text-green-600 font-medium">FABRIC</span>
                </div>
              )}
            </div>

            {biasedCut && biasAmount > 0 ? (
              <svg
                className="absolute bottom-0 left-0 right-0"
                style={{ height: weightBarHeight + 8 }}
                viewBox={`0 0 ${displayWidth} ${weightBarHeight + 8}`}
              >
                {(() => {
                  const biasVisual = Math.min(biasAmount * 2, 8);
                  // Bar droops lower on the taller side (higher Y = lower on screen)
                  const leftY = leftIsHigher ? biasVisual : 0;
                  const rightY = leftIsHigher ? 0 : biasVisual;
                  return (
                    <>
                      <polygon
                        points={`0,${leftY} ${displayWidth},${rightY} ${displayWidth},${rightY + weightBarHeight} 0,${leftY + weightBarHeight}`}
                        fill="#9ca3af"
                        stroke="#6b7280"
                        strokeWidth="0.5"
                      />
                      <text x={displayWidth / 2} y={(leftY + rightY) / 2 + weightBarHeight / 2 + 2} textAnchor="middle" fill="#fff" fontSize="6">
                        BAR
                      </text>
                      <text
                        x={leftIsHigher ? displayWidth - 2 : 2}
                        y={leftIsHigher ? rightY + 10 : leftY + 10}
                        textAnchor={leftIsHigher ? "end" : "start"}
                        fill="#f59e0b"
                        fontSize="7"
                        fontWeight="bold"
                      >
                        {biasAmount}"
                      </text>
                    </>
                  );
                })()}
              </svg>
            ) : (
              <div
                className="absolute bottom-0 left-0 right-0 bg-gray-400 flex items-center justify-center"
                style={{ height: weightBarHeight }}
              >
                <span className="text-[6px] text-white">BAR</span>
              </div>
            )}

            <div
              className={`absolute bg-yellow-400 text-[6px] text-black font-bold px-1 rounded ${
                item.motorSide === 'left' ? 'left-1' : 'right-1'
              }`}
              style={{ top: housingHeight + 2 }}
            >
              M
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2 justify-center text-[8px]">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-gray-400 rounded"></span> Housing/Bar
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-100 border border-green-300 rounded"></span> Fabric
        </span>
        {(hasLeftUChannel || hasRightUChannel) && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 border-2 border-blue-500 rounded"></span> U-Channel
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-yellow-400 rounded"></span> Motor ({item.motorSide})
        </span>
        {biasedCut && biasAmount > 0 && (
          <span className="flex items-center gap-1 text-orange-500 font-medium">
            Bias: {biasAmount}"
          </span>
        )}
      </div>
    </div>
  );
}

// Window Panel Details Component.
// Real data lives in `order_items.window_data` JSONB → API exposes as `windowData`.
// Earlier this read flat fields like `item.windowOrientation` that don't exist —
// always fell back to defaults (Horizontal, 1 window, no borders).
function WindowPanelDetails({ item }: { item: OrderItem }) {
  const hasWindow = (item as any).windowPanel || (item as any).window_panel;
  if (!hasWindow) return null;

  const wd: any = (item as any).windowData || (item as any).window_data || {};

  const orientation = wd.orientation || (item as any).windowOrientation || 'Horizontal';
  const windowCount = wd.windowCount || (item as any).windowCount || 1;
  const windowWidth = wd.windowWidth || (item as any).windowWidth;
  const windowHeight = wd.windowHeight || (item as any).windowHeight;
  const topBorder = wd.borderTop ?? wd.topBorderInches ?? (item as any).topBorderInches;
  const sideBorder = wd.borderSide ?? wd.sideBorderInches ?? (item as any).sideBorderInches;
  const bottomBorder = wd.borderBottom ?? wd.bottomBorderInches ?? (item as any).bottomBorderInches;
  const dividerType = wd.dividerType;
  const dividerSize = wd.dividerSize;

  return (
    <div className="mt-3 pt-3 border-t">
      <h6 className="text-xs font-semibold text-blue-600 mb-2">WINDOW PANEL</h6>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-gray-500">Orientation:</span> <span className="ml-2 font-medium capitalize">{orientation}</span></div>
        <div><span className="text-gray-500"># of Windows:</span> <span className="ml-2 font-medium">{windowCount}</span></div>
        {windowWidth && <div><span className="text-gray-500">Window Width:</span> <span className="ml-2 font-medium">{windowWidth}"</span></div>}
        {windowHeight && <div><span className="text-gray-500">Window Height:</span> <span className="ml-2 font-medium">{windowHeight}"</span></div>}
        {topBorder != null && topBorder !== '' && <div><span className="text-gray-500">Top Border:</span> <span className="ml-2 font-medium">{topBorder}"</span></div>}
        {sideBorder != null && sideBorder !== '' && <div><span className="text-gray-500">Side Border:</span> <span className="ml-2 font-medium">{sideBorder}"</span></div>}
        {bottomBorder != null && bottomBorder !== '' && <div><span className="text-gray-500">Bottom Border:</span> <span className="ml-2 font-medium">{bottomBorder}"</span></div>}
        {dividerType && <div><span className="text-gray-500">Divider:</span> <span className="ml-2 font-medium capitalize">{dividerType}{dividerType === 'divider' && dividerSize ? ` (${dividerSize}")` : ''}</span></div>}
      </div>
    </div>
  );
}

function OrderItemCard({ item, index, orderInfo, orderId, onEditItem }: { item: OrderItem; index: number; orderInfo: any; orderId: string; onEditItem?: (item: OrderItem) => void }) {
  const [expanded, setExpanded] = useState(false);

  const maxW = item.maxWidth || 0;
  const height = item.maxHeight || 0;
  const widthTop = item.widthTop || maxW;
  const widthMid = item.widthMiddle || maxW;
  const widthBot = item.widthBottom || maxW;
  const heightL = item.heightLeft || height;
  const heightC = item.heightCenter || height;
  const heightR = item.heightRight || height;

  // Inside mount uses smallest width, face mount uses largest
  const isInsideMount = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(item.trackMount || '');
  const widthValues = [item.widthTop, item.widthMiddle, item.widthBottom].filter((v): v is number => v != null && v > 0);
  const width = widthValues.length > 0
    ? (isInsideMount ? Math.min(...widthValues) : Math.max(...widthValues))
    : maxW;

  const hasUChannel = item.uChannel && item.uChannel !== 'none' && item.uChannel !== 'None';
  const applyDeductions = item.applyUChannelDeduction;
  let uChannelDeduction = 0;
  if (hasUChannel && applyDeductions) {
    uChannelDeduction = item.uChannel === 'two_sides' ? 0.75 : 0.375;
  }
  const dealerBoxAdj = item.boxWidthAdjustment || 0;

  // Track mount adjustment (+3 per wall side, +6 for wall/wall)
  let trackMountAdj = 0;
  const tm = item.trackMount || '';
  if (tm === 'wall') { trackMountAdj = 6; }
  else if (tm === 'inside_face_left' || tm === 'inside_face_right') { trackMountAdj = 3; }

  // System Width: for fabric, tracks, weight bar (uses min width + track mount adj - u-channel deduction)
  const systemWidth = width + trackMountAdj - uChannelDeduction;
  // Box Width: uses min (width) as base + dealer adjustment (carries top-vs-min diff)
  const boxWidthBase = width;
  const boxDeduction = (item.applyBoxDeduction !== false) ? 0.25 : 0;
  const boxWidth = boxWidthBase + trackMountAdj - boxDeduction + dealerBoxAdj;

  // Keep factoryWidth alias for existing display
  const factoryWidth = systemWidth;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <span className="font-medium">Unit #{item.itemNumber || index + 1}</span>
          {item.expertMode && (
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">Expert</span>
          )}
          <span className="text-gray-600">
            {width}" x {height}"
          </span>
          <span className="text-sm text-gray-500">
            {formatFabricType(item.fabricType)} - {item.fabricColor || '-'}
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <span className={`px-2 py-1 text-xs font-medium rounded ${
            item.status === 'complete' ? 'bg-green-100 text-green-800' :
            item.status === 'in_production' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {item.status || 'Pending'}
          </span>
          <span className="font-medium">${(item.totalPrice || item.unitPrice || 0).toFixed(2)}</span>
          {onEditItem && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditItem(item); }}
              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              title="Edit item measurements"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          <svg
            className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <ScreenUnitPreview item={item} />
            </div>

            <div className="lg:col-span-2 space-y-4">
              {/* Dimensions */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">DIMENSIONS {item.expertMode ? <span className="text-amber-600">(Expert Mode)</span> : ''}</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {item.expertMode ? (
                    <>
                      <div>
                        <span className="text-gray-500">System Width:</span>
                        <span className="ml-2 font-medium">{widthTop}"</span>
                      </div>
                      <div>
                        <span className="text-gray-500">System Height:</span>
                        <span className="ml-2 font-medium">{heightL}"</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-gray-500">Width:</span>
                        <span className="ml-2 font-medium">T: {widthTop}" | M: {widthMid}" | B: {widthBot}"</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Height:</span>
                        <span className="ml-2 font-medium">L: {heightL}" | C: {heightC}" | R: {heightR}"</span>
                      </div>
                    </>
                  )}
                </div>
                {/* Calculated Production Widths */}
                <div className="mt-2 pt-2 border-t">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">System Width:</span>
                      <span className="ml-2 font-semibold text-blue-700">{systemWidth.toFixed(3)}"</span>
                      {(uChannelDeduction > 0 || trackMountAdj > 0) && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({width}"
                          {trackMountAdj > 0 && ` +${trackMountAdj}" track adj`}
                          {uChannelDeduction > 0 && ` −${uChannelDeduction}" u-ch`}
                          )
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500">Box Width:</span>
                      <span className="ml-2 font-semibold text-indigo-700">{boxWidth.toFixed(3)}"</span>
                      {(boxDeduction > 0 || dealerBoxAdj !== 0 || trackMountAdj > 0 || boxWidthBase !== width) && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({boxWidthBase}"{boxWidthBase !== width ? ' top' : ''}
                          {trackMountAdj > 0 && ` +${trackMountAdj}" track adj`}
                          {boxDeduction > 0 && ` −${boxDeduction}" box ded`}
                          {dealerBoxAdj !== 0 && ` ${dealerBoxAdj > 0 ? '+' : ''}${dealerBoxAdj}" dealer adj`}
                          )
                        </span>
                      )}
                    </div>
                  </div>
                  {hasUChannel && !applyDeductions && (
                    <p className="text-xs text-gray-400 mt-1">U-Channel selected — no factory deductions applied</p>
                  )}
                </div>
              </div>

              {/* Material */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">MATERIAL</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Fabric Type:</span>
                    <span className="ml-2 font-medium">{formatFabricType(item.fabricType)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fabric Color:</span>
                    <span className="ml-2 font-medium">{item.fabricColor || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Frame Color:</span>
                    <span className="ml-2 font-medium">{item.systemColor || orderInfo.systemColor || 'Black'}</span>
                  </div>
                </div>
                {item.componentColors && Object.keys(item.componentColors).length > 0 && (() => {
                  const cc = item.componentColors;
                  const fmt = (color: string, ralKey: string) => {
                    if (color === 'custom' && cc[ralKey]) return `Custom (${cc[ralKey]})`;
                    return color.charAt(0).toUpperCase() + color.slice(1);
                  };
                  return (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-300 rounded text-xs">
                      <span className="font-semibold text-orange-700">Mixed Colors: </span>
                      {cc.housingColor && <span className="mr-3">Box={fmt(cc.housingColor, 'housingRalNumber')}</span>}
                      {cc.leftTrackColor && <span className="mr-3">L.Track={fmt(cc.leftTrackColor, 'leftTrackRalNumber')}</span>}
                      {cc.rightTrackColor && <span className="mr-3">R.Track={fmt(cc.rightTrackColor, 'rightTrackRalNumber')}</span>}
                      {cc.weightBarColor && <span className="mr-3">WBar={fmt(cc.weightBarColor, 'weightBarRalNumber')}</span>}
                      {cc.leftUChannelColor && <span className="mr-3">L.UCh={fmt(cc.leftUChannelColor, 'leftUChannelRalNumber')}</span>}
                      {cc.rightUChannelColor && <span className="mr-3">R.UCh={fmt(cc.rightUChannelColor, 'rightUChannelRalNumber')}</span>}
                    </div>
                  );
                })()}
              </div>

              {/* Installation */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">INSTALLATION</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Installation Type:</span>
                    <span className="ml-2 font-medium">{formatInstallationType(item.installationType)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Track Mount:</span>
                    <span className="ml-2 font-medium">{formatTrackMount(item.trackMount)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">U-Channel:</span>
                    <span className="ml-2 font-medium">{formatUChannel(item.uChannel)}</span>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">OPTIONS</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Motor Side:</span>
                    <span className="ml-2 font-medium capitalize">{item.motorSide || 'Right'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Brush Size:</span>
                    <span className="ml-2 font-medium">{formatBrushSize(item.brushSize || item.weightBarBrush || orderInfo.brushSize)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Biased Cut:</span>
                    <span className="ml-2 font-medium">
                      {item.biasedCut ? `Yes (${item.biasedCutInches || 0}"${item.biasedCutSide ? `, ${item.biasedCutSide === 'left' ? 'right' : 'left'} side shorter` : ''})` : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Window Panel:</span>
                    <span className="ml-2 font-medium">{item.windowPanel ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Recessed:</span>
                    <span className="ml-2 font-medium">{item.recessed ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Angled Bottom:</span>
                    <span className="ml-2 font-medium">{item.angledBottom ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Seam Required:</span>
                    <span className={`ml-2 font-medium ${item.seam || item.seamRequired ? 'text-yellow-600' : ''}`}>
                      {item.seam || item.seamRequired ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Screen Location */}
              {item.screenLocation && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-gray-600 mb-2">SCREEN LOCATION</h5>
                  <p className="text-sm font-medium">{item.screenLocation}</p>
                </div>
              )}

              {/* Window Panel Details */}
              <WindowPanelDetails item={item} />

              {/* Additional Details */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">ADDITIONAL DETAILS</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {item.motorType && (
                    <div>
                      <span className="text-gray-500">Motor Type:</span>
                      <span className="ml-2 font-medium">{motorLabel(item.motorType)}</span>
                    </div>
                  )}
                  {item.boxSize && (
                    <div>
                      <span className="text-gray-500">Box Size:</span>
                      <span className="ml-2 font-medium">{item.boxSize}</span>
                    </div>
                  )}
                  {item.weightBarSize && (
                    <div>
                      <span className="text-gray-500">Weight Bar:</span>
                      <span className="ml-2 font-medium">{item.weightBarSize}</span>
                    </div>
                  )}
                  {item.kederColor && (
                    <div>
                      <span className="text-gray-500">Keder Color:</span>
                      <span className="ml-2 font-medium">{item.kederColor}</span>
                    </div>
                  )}
                  {item.springs && (
                    <div>
                      <span className="text-gray-500">Springs:</span>
                      <span className="ml-2 font-medium">{item.springs} {item.springCount ? `(${item.springCount})` : ''}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">PRICE</h5>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Unit Price:</span>
                  <span className="font-medium">${(item.unitPrice || 0).toFixed(2)}</span>
                </div>
                {(item.uChannelPrice || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">U-Channel:</span>
                    <span className="font-medium">+${item.uChannelPrice!.toFixed(2)}</span>
                  </div>
                )}
                {(item.extrasPrice || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Extras:</span>
                    <span className="font-medium">+${item.extrasPrice!.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t mt-2 pt-2">
                  <span className="font-semibold">Total:</span>
                  <span className="font-bold">${(item.totalPrice || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {item.notes && !item.notes.startsWith('{') && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-yellow-700 mb-1">NOTES</h5>
                  <p className="text-sm text-yellow-800">{item.notes}</p>
                </div>
              )}

              {/* Installer Sheet Button */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => orderId && openInstallerSheet(orderId, item.itemNumber || index + 1)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Installer Sheet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineItem({ label, date }: { label: string; date: string }) {
  return (
    <div className="flex items-center space-x-3">
      <div className="w-2 h-2 bg-blue-500 rounded-full" />
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">
          {format(new Date(date), 'MMM d, yyyy h:mm a')}
        </p>
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatAddress(address: { street?: string; street2?: string; city?: string; state?: string; zip?: string; name?: string; contactName?: string } | string | null | undefined): string {
  if (!address) return '';
  if (typeof address === 'string') {
    try {
      const parsed = JSON.parse(address);
      return formatAddress(parsed);
    } catch {
      return address;
    }
  }
  if (typeof address !== 'object') return '';

  if (Object.keys(address).length === 0) return 'Not specified';

  const parts: string[] = [];
  if (address.name || address.contactName) parts.push(address.name || address.contactName || '');
  if (address.street) parts.push(address.street);
  if (address.street2) parts.push(address.street2);
  if (address.city || address.state || address.zip) {
    const cityLine = [address.city, address.state].filter(Boolean).join(', ');
    const fullLine = [cityLine, address.zip].filter(Boolean).join(' ');
    if (fullLine) parts.push(fullLine);
  }
  return parts.join('\n') || 'Not specified';
}

// QC Photos Tab Component
function QcPhotosTab({ records, loading, lightbox, setLightbox, onRefresh }: {
  records: any[];
  loading: boolean;
  lightbox: string | null;
  setLightbox: (url: string | null) => void;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading QC photos...</span>
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-gray-500 text-lg font-medium">No QC photos yet</p>
          <p className="text-gray-400 text-sm mt-1">Photos taken at workstations during production will appear here.</p>
        </div>
      </div>
    );
  }

  // Group records by workstation
  const grouped: Record<string, any[]> = {};
  records.forEach(r => {
    const key = r.workstationCode || 'Unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          QC Documentation ({records.length} record{records.length !== 1 ? 's' : ''})
        </h3>
        <button onClick={onRefresh} className="text-sm text-blue-600 hover:text-blue-800">
          Refresh
        </button>
      </div>

      {Object.entries(grouped).map(([station, recs]) => (
        <div key={station} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-800">{station}</h4>
            <span className="text-sm text-gray-500">{recs.length} record{recs.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {recs.map((rec: any) => (
                <div key={rec.id} className="group relative">
                  {rec.imageUrl ? (
                    <div
                      className="cursor-pointer"
                      onClick={() => setLightbox(rec.imageUrl)}
                    >
                      <img
                        src={rec.imageUrl}
                        alt={`QC - ${rec.stepName || station}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:border-blue-400 transition-colors"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-32 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="mt-1">
                    {rec.stepName && <p className="text-xs font-medium text-gray-700 truncate">{rec.stepName}</p>}
                    <p className="text-xs text-gray-500">{rec.takenByName || 'Unknown'}</p>
                    <p className="text-xs text-gray-400">{new Date(rec.takenAt).toLocaleString()}</p>
                    {rec.measurementLabel && (
                      <p className="text-xs mt-1">
                        <span className="text-gray-600">{rec.measurementLabel}: </span>
                        <span className={rec.pass ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          {rec.actualValue}{rec.unit ? ` ${rec.unit}` : ''}
                        </span>
                        {rec.expectedValue != null && (
                          <span className="text-gray-400"> (exp: {rec.expectedValue})</span>
                        )}
                      </p>
                    )}
                    {rec.notes && <p className="text-xs text-gray-500 italic mt-1 truncate">{rec.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Lightbox Modal */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
          >
            &times;
          </button>
          <img
            src={lightbox}
            alt="QC Photo Full Size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
